import { useState, useEffect, useCallback } from 'react';
import { UsersTab, OnlineTab } from "../components/admin-user-tabs";
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Network, Globe, ServerCog, UserCog, Power, FileText, type LucideIcon } from 'lucide-react';
import { LOGIN_PATH, adminApi } from '../lib/api';
import { PageBuilder } from '../components/PageBuilder';
import { MigrationTab } from '../components/MigrationTab';

const TOKEN_KEY = 'admin_token';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(adminApi(url), { ...options, headers: { ...authHeaders(), ...(options.headers as Record<string, string> || {}) } }).then(r => {
    if (r.status === 403) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('admin_email');
      window.location.href = LOGIN_PATH;
      throw new Error('Session expired');
    }
    return r;
  });
}

function formatTokens(n: number): string {
  if (!n || n === 0) return '-';
  if (n >= Number.MAX_SAFE_INTEGER) return '∞';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const STATUS_COLORS: Record<string, string> = {
  available: 'text-emerald-400 bg-emerald-500/10',
  unavailable: 'text-red-400 bg-red-500/10',
  unknown: 'text-gray-400 bg-gray-500/10',
  rate_limited: 'text-yellow-400 bg-yellow-500/10',
};

const FINDING_COLORS: Record<string, string> = {
  param_diff: 'text-amber-400 bg-amber-500/10',
  missing_local: 'text-cyan-400 bg-cyan-500/10',
  stale_local: 'text-orange-400 bg-orange-500/10',
  auto_fixed: 'text-emerald-400 bg-emerald-500/10',
  ambiguous: 'text-gray-400 bg-gray-500/10',
};

const TASK_STATUS_COLORS: Record<string, string> = {
  completed: 'text-emerald-400',
  running: 'text-cyan-400',
  failed: 'text-red-400',
  pending: 'text-gray-400',
};

type Tab = 'dashboard' | 'users' | 'online' | 'models' | 'vendors' | 'agent' | 'helpers' | 'news' | 'orders' | 'cards' | 'products' | 'subs' | 'pages' | 'downloads' | 'import' | 'payment' | 'email' | 'migration' | 'settings';

export function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [tabFilter, setTabFilter] = useState<any>(null);
  const goTab = (t: Tab, filter?: any) => { setTabFilter(filter || null); setTab(t); };
  const [authed, setAuthed] = useState<boolean>(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!authed) navigate(LOGIN_PATH);
  }, [authed, navigate]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('admin_email');
    navigate(LOGIN_PATH);
  };

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <Link to="/" className="text-xl font-bold gradient-text">CXAI-FreeRouter</Link>
        <div className="flex gap-4 text-sm">
          <Link to="/" className="text-gray-300 hover:text-cyan-400">首页</Link>
          <Link to="/models" className="text-gray-300 hover:text-cyan-400">模型目录</Link>
          <button onClick={logout} className="text-gray-400 hover:text-red-400">退出</button>
        </div>
      </nav>

      <div className="flex">
        <aside className="w-48 min-h-[calc(100vh-57px)] border-r border-gray-800 py-4">
          {([
            ['dashboard', '仪表盘'],
            ['users', '用户管理'],
            ['online', '在线状态'],
            ['models', '模型管理'],
            ['vendors', '厂商管理'],
            ['agent', '智能体'],
            ['helpers', 'AI 助手'],
            ['news', '厂商新闻'],
            ['orders', '订单管理'],
            ['cards', '卡密管理'],
            ['products', '产品管理'],
            ['subs', '授权管理'],


            ['pages', '页面设计'],
            ['downloads', '下载管理'],
            ['import', '数据导入'],
            ['payment', '支付设置'],
            ['email', '邮件队列'],
            ['migration', '迁移与冗余'],
            ['settings', '系统设置'],
          ] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`w-full text-left px-6 py-2.5 text-sm transition ${tab === key ? 'text-cyan-400 bg-cyan-500/5 border-r-2 border-cyan-500' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </aside>

        <main className="flex-1 p-6">
          {tab === 'dashboard' && <DashboardTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'online' && <OnlineTab />}
          {tab === 'models' && <ModelsTab />}
          {tab === 'vendors' && <VendorsTab />}
          {tab === 'agent' && <AgentTab />}
          {tab === 'helpers' && <HelpersTab />}
          {tab === 'news' && <NewsTab />}
          {tab === 'orders' && <OrdersTab initialFilter={tabFilter} />}
          {tab === 'cards' && <CardsTab initialFilter={tabFilter} />}
          {tab === 'products' && <ProductsTab goTab={goTab} />}
          {tab === 'subs' && <SubsTab initialFilter={tabFilter} />}

          {tab === 'pages' && <PageBuilder />}
          {tab === 'downloads' && <DownloadsTab />}
          {tab === 'import' && <ImportTab />}
          {tab === 'payment' && <PaymentTab />}

          {tab === 'email' && <EmailTab />}
          {tab === 'migration' && <MigrationTab />}
          {tab === 'settings' && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState<any>(null);

  const load = useCallback(() => {
    apiFetch('/api/admin/dashboard').then(r => r.json()).then(setData).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-gray-500">加载中...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">仪表盘</h1>
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="模型总数" value={data.totalModels} color="text-cyan-400" />
        <StatCard label="可用模型" value={data.availableModels} color="text-emerald-400" />
        <StatCard label="不可用" value={data.unavailableModels} color="text-red-400" />
        <StatCard label="未知状态" value={data.unknownModels} color="text-gray-400" />
        <StatCard label="厂商数量" value={data.totalVendors} color="text-blue-400" />
        <StatCard label="免费 Tokens/月" value={formatTokens(data.totalFreeTokens)} color="text-purple-400" />
        <StatCard label="活跃订阅" value={data.subscriptions} color="text-amber-400" />
        <StatCard label="新闻条数" value={data.newsCount} color="text-pink-400" />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-3">最近任务</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
            {(data.recentTasks || []).map((t: any) => (
              <div key={t.id} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-800/50">
                <span className="text-gray-300">{t.type}</span>
                <span className={`text-xs ${TASK_STATUS_COLORS[t.status] || 'text-gray-400'}`}>{t.status}</span>
                <span className="text-gray-500 text-xs">{t.duration_ms ? `${(t.duration_ms / 1000).toFixed(1)}s` : '-'}</span>
              </div>
            ))}
            {(!data.recentTasks || data.recentTasks.length === 0) && <div className="text-gray-600 text-sm">暂无任务</div>}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-3">最近日志</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
            {(data.recentLogs || []).map((l: any) => (
              <div key={l.id} className="py-1 text-sm border-b border-gray-800/50">
                <span className={`text-xs mr-2 ${l.level === 'error' ? 'text-red-400' : l.level === 'warn' ? 'text-yellow-400' : 'text-gray-500'}`}>[{l.level}]</span>
                <span className="text-gray-300">{l.message}</span>
              </div>
            ))}
            {(!data.recentLogs || data.recentLogs.length === 0) && <div className="text-gray-600 text-sm">暂无日志</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: any; color: string }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function ModelsTab() {
  const [models, setModels] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [fPlatform, setFPlatform] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fSource, setFSource] = useState('');
  const [fVis, setFVis] = useState('');
  const [fEnabled, setFEnabled] = useState('');
  const [fScore, setFScore] = useState('');
  const [fQuota, setFQuota] = useState('');
  const [vendors, setVendors] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDup, setShowDup] = useState(false);
  const [editModel, setEditModel] = useState<any | null>(null);

  useEffect(() => { apiFetch('/api/admin/vendors').then(r => r.json()).then(setVendors).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: '50' });
    if (search) params.set('search', search);
    if (fPlatform) params.set('platform', fPlatform);
    if (fCategory) params.set('category', fCategory);
    if (fStatus) params.set('status', fStatus);
    if (fSource) params.set('source', fSource);
    if (fVis) params.set('visibility', fVis);
    if (fEnabled) params.set('enabled', fEnabled);
    if (fScore) params.set('score', fScore);
    if (fQuota) params.set('hasQuota', fQuota);
    apiFetch(`/api/admin/models?${params}`).then(r => r.json()).then(d => { setModels(d.data || []); setTotal(d.total || 0); }).catch(() => {});
  }, [page, search, fPlatform, fCategory, fStatus, fSource, fVis, fEnabled, fScore, fQuota]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!confirm('确认删除？')) return;
    await apiFetch(`/api/admin/models/${id}`, { method: 'DELETE' });
    load();
  };

  const handleScore = async (id: number, score: number) => {
    await apiFetch(`/api/admin/models/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quality_score: score }) });
    load();
  };

  const handleVis = async (id: number, vis: string) => {
    await apiFetch(`/api/admin/models/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sync_visibility: vis }) });
    load();
  };

  const renderScore = (m: any) => {
    const s = Math.round(Number(m.quality_score) || 0);
    const label = m.quality_source === 'manual' ? '手动' : (m.quality_source === 'cloud' ? '云端' : (m.quality_source || '未评分'));
    return (
      <div className="flex items-center justify-center gap-1">
        <input
          key={`${m.id}-${s}`}
          type="number" min={0} max={100} step={5} defaultValue={s}
          onBlur={e => {
            const v = Math.round(Number(e.target.value));
            if (Number.isFinite(v) && v >= 0 && v <= 100 && v !== s) handleScore(m.id, v);
          }}
          className="w-16 bg-gray-900 border border-gray-800 rounded text-xs px-1 py-0.5 text-center"
          title={`0-100，越大越强；来源：${label}`}
        />
        <span className="text-[10px] text-gray-600">{label}</span>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">模型管理 <span className="text-sm text-gray-500">({total})</span></h1>
        <div className="flex gap-2">
          <button onClick={() => setShowDup(true)} className="px-4 py-2 bg-amber-500/15 text-amber-400 rounded-lg text-sm hover:bg-amber-500/25">模型去重</button>
          <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">{showAdd ? '取消' : '添加模型'}</button>
        </div>
      </div>

      {showDup && <DupDialog type="models" onClose={() => setShowDup(false)} onDone={load} />}
      {showAdd && <AddModelForm onDone={() => { setShowAdd(false); load(); }} />}
      {editModel && <EditModelDialog model={editModel} vendors={vendors} onClose={() => setEditModel(null)} onDone={() => { setEditModel(null); load(); }} />}

      <div className="flex flex-wrap gap-2 mb-4">
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="搜索模型ID/名称..." className="flex-1 min-w-[200px] px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
        <select value={fPlatform} onChange={e => { setFPlatform(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">全部厂商</option>
          {vendors.map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={fCategory} onChange={e => { setFCategory(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">全部类别</option>
          <option value="chat">chat</option><option value="code">code</option><option value="embedding">embedding</option><option value="image">image</option><option value="vision">vision</option><option value="audio">audio</option>
        </select>
        <select value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">全部状态</option>
          <option value="available">available</option><option value="unavailable">unavailable</option><option value="unknown">unknown</option><option value="rate_limited">rate_limited</option>
        </select>
        <select value={fSource} onChange={e => { setFSource(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">全部来源</option>
          <option value="seed">seed</option><option value="agent">agent</option><option value="manual">manual</option><option value="import">import</option>
        </select>
        <select value={fVis} onChange={e => { setFVis(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">可见性:全部</option>
          <option value="free">免费版可同步</option><option value="paid">仅付费可同步</option>
        </select>
        <select value={fEnabled} onChange={e => { setFEnabled(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">启用:全部</option>
          <option value="1">已启用</option><option value="0">已禁用</option>
        </select>
        <select value={fScore} onChange={e => { setFScore(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">评分:全部</option>
          <option value="high">高分（80 以上）</option>
          <option value="mid">中等（50-79）</option>
          <option value="low">偏低（50 以下）</option>
          <option value="unrated">未评分</option>
        </select>
        <select value={fQuota} onChange={e => { setFQuota(e.target.value); setPage(1); }} className="px-2 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="">免费额度:全部</option>
          <option value="yes">有免费额度</option><option value="no">无免费额度</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 text-left">厂商</th>
              <th className="py-2 text-left">模型ID</th>
              <th className="py-2 text-left">名称</th>
              <th className="py-2 text-left">类别</th>
              <th className="py-2 text-right">免费额度</th>
              <th className="py-2 text-center">状态</th>
              <th className="py-2 text-center">评分</th>
              <th className="py-2 text-center">同步可见性</th>
              <th className="py-2 text-center">来源</th>
              <th className="py-2 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m: any) => (
              <tr key={m.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-300">{m.vendor_name || m.platform}</td>
                <td className="py-2 text-gray-400 font-mono text-xs">{m.model_id}</td>
                <td className="py-2 text-gray-300">{m.display_name}</td>
                <td className="py-2 text-gray-500">{m.category}</td>
                <td className="py-2 text-right text-cyan-400">{m.free_quota_label || formatTokens(m.free_quota)}</td>
                <td className="py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[m.availability_status] || STATUS_COLORS.unknown}`}>{m.availability_status}</span>
                </td>
                <td className="py-2 text-center">{renderScore(m)}</td>
                <td className="py-2 text-center">
                  <select value={m.sync_visibility || 'free'} onChange={e => handleVis(m.id, e.target.value)} className={`text-xs px-2 py-1 rounded cursor-pointer border ${(m.sync_visibility || 'free') === 'paid' ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'bg-gray-900 border-gray-800 text-emerald-400'}`}>
                    <option value="free">免费版可同步</option>
                    <option value="paid">仅付费可同步</option>
                  </select>
                </td>
                <td className="py-2 text-center text-gray-500 text-xs">{m.source}</td>
                <td className="py-2 text-center whitespace-nowrap">
                  <button onClick={() => setEditModel(m)} className="text-xs text-cyan-400 hover:text-cyan-300 mr-2">编辑</button>
                  <button onClick={() => handleDelete(m.id)} className="text-xs text-red-400 hover:text-red-300">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > 50 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">上一页</button>
          <span className="text-sm text-gray-400">第 {page} 页</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">下一页</button>
        </div>
      )}
    </div>
  );
}

function AddModelForm({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ platform: '', modelId: '', displayName: '', category: 'chat', freeQuotaLabel: '', contextWindow: '', intelligenceRank: '10', supportsVision: false, supportsTools: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await apiFetch('/api/admin/models', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: form.platform,
        modelId: form.modelId,
        displayName: form.displayName || form.modelId,
        category: form.category,
        freeQuotaLabel: form.freeQuotaLabel,
        contextWindow: form.contextWindow ? parseInt(form.contextWindow) : null,
        intelligenceRank: parseInt(form.intelligenceRank),
        supportsVision: form.supportsVision,
        supportsTools: form.supportsTools,
        capabilities: [],
      }),
    });
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4 grid grid-cols-3 gap-3">
      <input value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} placeholder="厂商ID *" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" required />
      <input value={form.modelId} onChange={e => setForm({ ...form, modelId: e.target.value })} placeholder="模型ID *" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" required />
      <input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} placeholder="显示名称" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
      <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
        <option value="chat">chat</option><option value="code">code</option><option value="embedding">embedding</option><option value="image">image</option><option value="vision">vision</option>
      </select>
      <input value={form.freeQuotaLabel} onChange={e => setForm({ ...form, freeQuotaLabel: e.target.value })} placeholder="免费额度 (如 ~30M)" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
      <input value={form.contextWindow} onChange={e => setForm({ ...form, contextWindow: e.target.value })} placeholder="上下文窗口" type="number" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
      <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={form.supportsVision} onChange={e => setForm({ ...form, supportsVision: e.target.checked })} /> 视觉</label>
      <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={form.supportsTools} onChange={e => setForm({ ...form, supportsTools: e.target.checked })} /> 工具调用</label>
      <button type="submit" className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">保存</button>
    </form>
  );
}

function EditModelDialog({ model, vendors, onClose, onDone }: { model: any; vendors: any[]; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    platform: model.platform || '',
    display_name: model.display_name || '',
    category: model.category || 'chat',
    free_quota_label: model.free_quota_label || '',
    free_quota: String(model.free_quota || 0),
    context_window: model.context_window ? String(model.context_window) : '',
    intelligence_rank: String(model.intelligence_rank ?? 10),
    availability_status: model.availability_status || 'unknown',
    enabled: model.enabled ?? 1,
    sync_visibility: model.sync_visibility || 'free',
    quality_score: String(model.quality_score ?? 0),
    supports_vision: !!model.supports_vision,
    supports_tools: !!model.supports_tools,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await apiFetch(`/api/admin/models/${model.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: form.platform,
        display_name: form.display_name,
        category: form.category,
        free_quota_label: form.free_quota_label,
        free_quota: Number(form.free_quota) || 0,
        context_window: form.context_window ? Number(form.context_window) : null,
        intelligence_rank: Number(form.intelligence_rank) || 10,
        availability_status: form.availability_status,
        enabled: form.enabled ? 1 : 0,
        sync_visibility: form.sync_visibility,
        quality_score: Number(form.quality_score) || 0,
        supports_vision: form.supports_vision,
        supports_tools: form.supports_tools,
      }),
    });
    setSaving(false);
    onDone();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-[600px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">编辑模型</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">✕</button>
        </div>
        <div className="text-xs text-gray-500 mb-4 font-mono">{model.platform} / {model.model_id}</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">厂商</label>
            <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white">
              {vendors.map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">显示名称</label>
            <input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">类别</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white">
              <option value="chat">chat</option><option value="code">code</option><option value="embedding">embedding</option><option value="image">image</option><option value="vision">vision</option><option value="audio">audio</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">免费额度标签</label>
            <input value={form.free_quota_label} onChange={e => setForm({ ...form, free_quota_label: e.target.value })} placeholder="如 ~30M tokens" className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">免费额度数值</label>
            <input value={form.free_quota} onChange={e => setForm({ ...form, free_quota: e.target.value })} type="number" className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">上下文窗口</label>
            <input value={form.context_window} onChange={e => setForm({ ...form, context_window: e.target.value })} type="number" className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">智能排名</label>
            <input value={form.intelligence_rank} onChange={e => setForm({ ...form, intelligence_rank: e.target.value })} type="number" className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">可用性状态</label>
            <select value={form.availability_status} onChange={e => setForm({ ...form, availability_status: e.target.value })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white">
              <option value="available">available</option><option value="unavailable">unavailable</option><option value="unknown">unknown</option><option value="rate_limited">rate_limited</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">启用状态</label>
            <select value={form.enabled ? 1 : 0} onChange={e => setForm({ ...form, enabled: Number(e.target.value) })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white">
              <option value={1}>已启用</option><option value={0}>已禁用</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">同步可见性</label>
            <select value={form.sync_visibility} onChange={e => setForm({ ...form, sync_visibility: e.target.value })} className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white">
              <option value="free">免费版可同步</option><option value="paid">仅付费可同步</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">评分（0-100，越大越强，0 = 最弱）</label>
            <input
              type="number" min={0} max={100} step={5}
              value={form.quality_score}
              onChange={e => setForm({ ...form, quality_score: e.target.value })}
              className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white"
            />
            <p className="text-[11px] text-gray-600 mt-1">手动输入会标为 manual，自动评分不会覆盖</p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={form.supports_vision} onChange={e => setForm({ ...form, supports_vision: e.target.checked })} /> 视觉</label>
            <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={form.supports_tools} onChange={e => setForm({ ...form, supports_tools: e.target.checked })} /> 工具调用</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-gray-400 rounded text-sm hover:bg-gray-700">取消</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30 disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
        </div>
      </div>
    </div>
  );
}

function VendorsTab() {
  const [vendors, setVendors] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDup, setShowDup] = useState(false);
  const [editVendor, setEditVendor] = useState<any | null>(null);

  const load = useCallback(() => {
    apiFetch('/api/admin/vendors').then(r => r.json()).then(setVendors).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (v: any) => {
    if (!confirm(`确认删除厂商「${v.name}」(${v.id})？\n该厂商下若有模型将无法删除，需先迁移或删除模型。`)) return;
    const r = await apiFetch(`/api/admin/vendors/${encodeURIComponent(v.id)}`, { method: 'DELETE' });
    if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.error || '删除失败'); return; }
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">厂商管理 <span className="text-sm text-gray-500">({vendors.length})</span></h1>
        <div className="flex gap-2">
          <button onClick={() => setShowDup(true)} className="px-4 py-2 bg-amber-500/15 text-amber-400 rounded-lg text-sm hover:bg-amber-500/25">厂商去重</button>
          <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">{showAdd ? '取消' : '添加厂商'}</button>
        </div>
      </div>

      {showDup && <DupDialog type="vendors" onClose={() => setShowDup(false)} onDone={load} />}

      {showAdd && <VendorForm onDone={() => { setShowAdd(false); load(); }} />}

      {editVendor && <VendorForm initial={editVendor} onDone={() => { setEditVendor(null); load(); }} />}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 text-left">ID</th>
              <th className="py-2 text-left">名称</th>
              <th className="py-2 text-left">Base URL</th>
              <th className="py-2 text-center">需要Key</th>
              <th className="py-2 text-center">区域</th>
              <th className="py-2 text-center">状态</th>
              <th className="py-2 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v: any) => (
              <tr key={v.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-300 font-mono text-xs">{v.id}</td>
                <td className="py-2 text-gray-300">{v.name}</td>
                <td className="py-2 text-gray-500 font-mono text-xs truncate max-w-xs">{v.base_url}</td>
                <td className="py-2 text-center">{v.requires_key ? '✓' : '✗'}</td>
                <td className="py-2 text-center text-gray-400">{v.region}</td>
                <td className="py-2 text-center"><span className={`text-xs ${v.status === 'active' ? 'text-emerald-400' : 'text-gray-400'}`}>{v.status}</span></td>
                <td className="py-2 text-center whitespace-nowrap">
                  <button onClick={() => setEditVendor(v)} className="text-cyan-400 hover:text-cyan-300 text-xs px-2">编辑</button>
                  <button onClick={() => handleDelete(v)} className="text-red-400 hover:text-red-300 text-xs px-2">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorForm({ initial, onDone }: { initial?: any; onDone: () => void }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    id: initial?.id || '',
    name: initial?.name || '',
    baseUrl: initial?.base_url || '',
    websiteUrl: initial?.website_url || '',
    requiresKey: initial?.requires_key ?? true,
    region: initial?.region || 'intl',
    description: initial?.description || '',
    freeTierInfo: initial?.free_tier_info || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await apiFetch('/api/admin/vendors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.error || '保存失败'); return; }
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4 grid grid-cols-2 gap-3">
      <input value={form.id} onChange={e => setForm({ ...form, id: e.target.value })} placeholder="厂商ID *" disabled={isEdit} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm disabled:opacity-50" required />
      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="名称 *" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" required />
      <input value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} placeholder="Base URL *" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm col-span-2" required />
      <input value={form.websiteUrl} onChange={e => setForm({ ...form, websiteUrl: e.target.value })} placeholder="官网URL" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm col-span-2" />
      <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
        <option value="intl">国际</option><option value="cn">国内</option>
      </select>
      <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={form.requiresKey} onChange={e => setForm({ ...form, requiresKey: e.target.checked })} /> 需要API Key</label>
      <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="描述" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm col-span-2" />
      <input value={form.freeTierInfo} onChange={e => setForm({ ...form, freeTierInfo: e.target.value })} placeholder="免费额度说明" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm col-span-2" />
      <div className="col-span-2 flex gap-2">
        <button type="submit" className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">{isEdit ? '保存修改' : '保存'}</button>
        <button type="button" onClick={onDone} className="px-4 py-1.5 bg-gray-800 text-gray-400 rounded text-sm hover:bg-gray-700">取消</button>
      </div>
    </form>
  );
}

function AgentTab() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [running, setRunning] = useState(false);
  const [cfg, setCfg] = useState<any>(null);
  const [form, setForm] = useState({ scheduleEnabled: true, timesPerDay: 4, maxMinutes: 30, llmEnabled: false, llmBaseUrl: '', llmApiKey: '', llmModel: '' });
  const [saveMsg, setSaveMsg] = useState('');
  const [llmTest, setLlmTest] = useState<{ ok?: boolean; message?: string } | null>(null);
  const [scoringSources, setScoringSources] = useState<any[]>([]);
  const [scoreForm, setScoreForm] = useState({ name: '', url: '' });
  const [auditRuns, setAuditRuns] = useState<any[]>([]);
  const [auditFindings, setAuditFindings] = useState<any[]>([]);

  const loadScoring = useCallback(() => {
    apiFetch('/api/admin/scoring-sources').then(r => r.json()).then(setScoringSources).catch(() => {});
  }, []);

  const loadAudit = useCallback(() => {
    apiFetch('/api/admin/audit-runs?limit=3').then(r => r.json()).then(d => setAuditRuns(d.data || [])).catch(() => {});
    apiFetch('/api/admin/audit-findings?limit=60').then(r => r.json()).then(d => setAuditFindings(d.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    apiFetch('/api/admin/tasks').then(r => r.json()).then(setTasks).catch(() => {});
    apiFetch('/api/admin/logs?limit=200').then(r => r.json()).then(setLogs).catch(() => {});
  }, []);

  const loadCfg = useCallback(() => {
    apiFetch('/api/admin/agent-settings').then(r => r.json()).then(d => {
      setCfg(d);
      setForm({
        scheduleEnabled: d.schedule?.enabled ?? true,
        timesPerDay: d.schedule?.timesPerDay ?? 4,
        maxMinutes: d.schedule?.maxMinutes ?? 30,
        llmEnabled: d.llm?.enabled ?? false,
        llmBaseUrl: d.llm?.baseUrl ?? '',
        llmApiKey: '',
        llmModel: d.llm?.model ?? '',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    loadCfg();
    loadScoring();
    loadAudit();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load, loadCfg, loadScoring, loadAudit]);

  const runAgent = async () => {
    setRunning(true);
    await apiFetch('/api/admin/agent/run', { method: 'POST' });
    setTimeout(() => { setRunning(false); load(); }, 2000);
  };

  const runProbe = async () => {
    setRunning(true);
    await apiFetch('/api/admin/agent/probe', { method: 'POST' });
    setTimeout(() => { setRunning(false); load(); }, 2000);
  };

  const runScoring = async () => {
    setRunning(true);
    setSaveMsg('评分抓取已启动…');
    await apiFetch('/api/admin/agent/scoring-run', { method: 'POST' });
    setTimeout(() => { setRunning(false); load(); loadScoring(); }, 2500);
  };

  const runProvenance = async () => {
    setRunning(true);
    setSaveMsg('溯源盘点已启动…');
    await apiFetch('/api/admin/agent/provenance-run', { method: 'POST' });
    setTimeout(() => { setRunning(false); load(); loadAudit(); }, 3000);
  };

  const saveCfg = async () => {
    setSaveMsg('');
    const r = await apiFetch('/api/admin/agent-settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const d = await r.json();
    if (!r.ok) { setSaveMsg('保存失败：' + (d.error || '')); return; }
    setSaveMsg(d.message || '已保存');
    loadCfg();
  };

  const testLlm = async () => {
    setLlmTest(null);
    const r = await apiFetch('/api/admin/agent-llm-test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ baseUrl: form.llmBaseUrl, apiKey: form.llmApiKey, model: form.llmModel }),
    });
    const d = await r.json();
    setLlmTest({ ok: d.ok, message: d.message });
  };

  const addScoringSource = async () => {
    if (!scoreForm.url.trim()) return;
    const r = await apiFetch('/api/admin/scoring-sources', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scoreForm),
    });
    const d = await r.json();
    if (!r.ok) { setSaveMsg('添加评分源失败：' + (d.error || '')); return; }
    setScoreForm({ name: '', url: '' });
    setSaveMsg('评分源已添加，下次智能体周期自动抓取');
    loadScoring();
  };

  const delScoringSource = async (id: number) => {
    if (!confirm('确认删除该评分源？')) return;
    await apiFetch(`/api/admin/scoring-sources/${id}`, { method: 'DELETE' });
    loadScoring();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">智能体</h1>
        <div className="flex gap-3">
          <button onClick={runAgent} disabled={running} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 disabled:opacity-50">{running ? '运行中...' : '运行完整周期'}</button>
          <button onClick={runProbe} disabled={running} className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 disabled:opacity-50">仅探测</button>
          <button onClick={runScoring} disabled={running} className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm hover:bg-amber-500/30 disabled:opacity-50">立即抓取评分</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="总任务" value={tasks.length} color="text-cyan-400" />
        <StatCard label="成功" value={tasks.filter(t => t.status === 'completed').length} color="text-emerald-400" />
        <StatCard label="失败" value={tasks.filter(t => t.status === 'failed').length} color="text-red-400" />
      </div>

      {/* 运行计划 + 大模型接入 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-100">运行计划（定时）</h2>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={form.scheduleEnabled} onChange={e => setForm({ ...form, scheduleEnabled: e.target.checked })} className="accent-cyan-500" />
              启用定时
            </label>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-400 block mb-1">每天运行次数</label>
              <select value={form.timesPerDay} onChange={e => setForm({ ...form, timesPerDay: Number(e.target.value) })}
                className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
                {[1, 2, 3, 4, 6, 8, 12, 24].map(n => (
                  <option key={n} value={n}>{n} 次 / 天（每 {24 / n} 小时一次）</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">每次最长运行（分钟）</label>
              <input type="number" min={1} max={240} value={form.maxMinutes} onChange={e => setForm({ ...form, maxMinutes: Number(e.target.value) })}
                className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
              <p className="text-xs text-gray-500 mt-1">到达上限后自动跳过未完成的阶段，避免智能体长时间占用资源</p>
            </div>
            {cfg?.schedule?.nextRunAt && form.scheduleEnabled && (
              <div className="text-xs text-cyan-400 bg-cyan-500/5 border border-cyan-500/20 rounded px-3 py-2">
                下次自动运行：{new Date(cfg.schedule.nextRunAt).toLocaleString('zh-CN')}
              </div>
            )}
            {!form.scheduleEnabled && <div className="text-xs text-yellow-400/80">定时已暂停：智能体不会自动运行，可手动点「运行完整周期」</div>}
          </div>
        </div>

        <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-100">大模型接入</h2>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={form.llmEnabled} onChange={e => setForm({ ...form, llmEnabled: e.target.checked })} className="accent-cyan-500" />
              启用
            </label>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-400 block mb-1">API Base URL（OpenAI 兼容）</label>
              <input value={form.llmBaseUrl} onChange={e => setForm({ ...form, llmBaseUrl: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm font-mono" placeholder="https://api.openai.com/v1 或本地 http://127.0.0.1:8080/v1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 block mb-1">API Key</label>
                <input type="password" value={form.llmApiKey} onChange={e => setForm({ ...form, llmApiKey: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder={cfg?.llm?.hasKey ? '已配置（留空不修改）' : 'sk-...'} />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">模型名称</label>
                <input value={form.llmModel} onChange={e => setForm({ ...form, llmModel: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm font-mono" placeholder="gpt-4o-mini / deepseek-chat" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={testLlm} className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded text-sm hover:bg-blue-500/30">测试连接</button>
              {llmTest && <span className={`text-xs ${llmTest.ok ? 'text-emerald-400' : 'text-red-400'}`}>{llmTest.message}</span>}
            </div>
            <p className="text-xs text-gray-500">这是智能体的<b className="text-gray-400">专用模型</b>：智能体用它干活——爬取厂商/模型数据后的智能解析、新闻自动摘要与过滤、数据治理判断；同时供公开端<b className="text-gray-400">智能客服</b>回答用户问题（无法解决时自动引导转真人客服）。支持 OpenAI / DeepSeek / 硅基流动 / 本地 llama.cpp / Ollama 等任意兼容端点；未配置时智能体以规则模式运行。</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <button onClick={saveCfg} className="px-5 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">保存智能体配置</button>
        {saveMsg && <span className="text-xs text-emerald-400">{saveMsg}</span>}
      </div>

      {/* 模型评分源（智能体自动抓取模型好差评分） */}
      <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-100">模型评分源</h2>
          <span className="text-xs text-gray-500">智能体周期内自动抓取，按模型名/ID 匹配写入评分（0-5★），最新源覆盖旧评分</span>
        </div>
        <div className="flex gap-2 mb-3">
          <input value={scoreForm.name} onChange={e => setScoreForm({ ...scoreForm, name: e.target.value })} placeholder="评分源名称（可选）" className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
          <input value={scoreForm.url} onChange={e => setScoreForm({ ...scoreForm, url: e.target.value })} placeholder="JSON 数据 URL（[{model, score}]）" className="flex-[2] px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm font-mono" />
          <button onClick={addScoringSource} className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 rounded text-sm hover:bg-emerald-500/30">添加</button>
        </div>
        {scoringSources.length > 0 && (
          <div className="space-y-1.5">
            {scoringSources.map((s: any) => (
              <div key={s.id} className="flex items-center gap-3 text-sm bg-gray-900/50 border border-gray-800 rounded px-3 py-2">
                <span className={`text-xs w-12 ${s.status === 'ok' ? 'text-emerald-400' : s.status === 'error' ? 'text-red-400' : 'text-gray-500'}`}>{s.status || 'idle'}</span>
                <span className="text-gray-300 w-28 truncate">{s.name}</span>
                <span className="text-gray-500 flex-1 truncate font-mono text-xs">{s.url}</span>
                <span className="text-gray-600 text-xs truncate max-w-[200px]">{s.last_result || ''}</span>
                <button onClick={() => delScoringSource(s.id)} className="text-xs text-red-400 hover:text-red-300">删除</button>
              </div>
            ))}
          </div>
        )}
        {scoringSources.length === 0 && <p className="text-xs text-gray-500">尚未配置评分源。可填写任意返回 JSON 的评分接口（数组 {'[{model,score}]'} 或 {'{models:[...]}'}），例如 LMArena 榜单导出、评测聚合站接口。</p>}
      </div>

      {/* 模型溯源盘点（跨厂商参数比对 + 厂商覆盖摸底 + 多源校验） */}
      <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-100">模型溯源盘点</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">同模型跨厂商参数/额度比对 · 厂商端点覆盖摸底 · 多源交叉校验，确保数据准确度</span>
            <button onClick={runProvenance} disabled={running} className="px-4 py-1.5 bg-violet-500/20 text-violet-400 rounded text-sm hover:bg-violet-500/30 disabled:opacity-50">立即溯源盘点</button>
          </div>
        </div>
        {auditRuns.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {auditRuns.map((r: any) => (
              <div key={r.id} className="flex items-center gap-3 text-sm bg-gray-900/50 border border-gray-800 rounded px-3 py-2">
                <span className="text-gray-600 text-xs w-36">{r.run_at}</span>
                <span className="text-gray-300 flex-1 truncate">
                  {r.summary ? `模型分组 ${r.summary.groups ?? 0} · 跨厂商 ${r.summary.multiGroups ?? 0} 组 · 厂商摸底 ${r.summary.vendorsAudited ?? 0} · 发现 ${r.findings_count ?? 0} 项${r.summary.autoFixed ? ` · 自动补全 ${r.summary.autoFixed}` : ''}` : `发现 ${r.findings_count ?? 0} 项`}
                </span>
              </div>
            ))}
          </div>
        )}
        {auditFindings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="py-1.5 text-left">模型</th>
                  <th className="py-1.5 text-left">厂商</th>
                  <th className="py-1.5 text-left">字段</th>
                  <th className="py-1.5 text-left">类型</th>
                  <th className="py-1.5 text-left">详情</th>
                </tr>
              </thead>
              <tbody>
                {auditFindings.map((f: any) => (
                  <tr key={f.id} className="border-b border-gray-800/50">
                    <td className="py-1.5 text-gray-300 max-w-[180px] truncate font-mono">{f.model_id}</td>
                    <td className="py-1.5 text-gray-500 max-w-[140px] truncate">{f.platforms}</td>
                    <td className="py-1.5 text-gray-400">{f.field}</td>
                    <td className="py-1.5">
                      <span className={`text-[0.65rem] px-1.5 py-0.5 rounded ${FINDING_COLORS[f.finding_type] || 'bg-gray-800 text-gray-400'}`}>{f.finding_type}</span>
                    </td>
                    <td className="py-1.5 text-gray-500 max-w-[360px] truncate" title={f.detail}>{f.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-gray-500">暂无溯源记录。点击「立即溯源盘点」开始对全部模型做跨厂商比对与厂商覆盖摸底。</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-3">任务列表</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
            {tasks.map((t: any) => (
              <div key={t.id} className="flex items-center gap-3 py-1.5 text-sm border-b border-gray-800/50">
                <span className={`text-xs w-16 ${TASK_STATUS_COLORS[t.status] || 'text-gray-400'}`}>{t.status}</span>
                <span className="text-gray-300 flex-1 truncate">{t.type}</span>
                <span className="text-gray-500 text-xs truncate max-w-xs">{t.target}</span>
                <span className="text-gray-600 text-xs">{t.duration_ms ? `${(t.duration_ms / 1000).toFixed(1)}s` : ''}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-3">实时日志</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
            {logs.map((l: any) => (
              <div key={l.id} className="py-1 text-sm border-b border-gray-800/50">
                <span className="text-gray-600 text-xs mr-2">{l.created_at?.slice(11, 19)}</span>
                <span className={`text-xs mr-2 ${l.level === 'error' ? 'text-red-400' : l.level === 'warn' ? 'text-yellow-400' : 'text-gray-500'}`}>[{l.level}]</span>
                <span className="text-gray-300">{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewsTab() {
  const [news, setNews] = useState<any[]>([]);

  useEffect(() => {
    apiFetch('/api/admin/news').then(r => r.json()).then(setNews).catch(() => {});
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">厂商新闻 <span className="text-sm text-gray-500">({news.length})</span></h1>
      <div className="space-y-3">
        {news.map((n: any) => (
          <div key={n.id} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">{n.vendor}</span>
              <span className="text-xs text-gray-500">{n.published_at?.slice(0, 10)}</span>
            </div>
            <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-gray-200 hover:text-cyan-400 text-sm">{n.title}</a>
            {n.summary && <p className="text-gray-500 text-xs mt-1">{n.summary}</p>}
          </div>
        ))}
        {news.length === 0 && <div className="text-gray-600">暂无新闻</div>}
      </div>
    </div>
  );
}

const HELPER_STAGE_LABELS: Record<string, string> = {
  crawl: '数据采集',
  news: '新闻情报',
  probe: '可用性探测',
  scoring: '评分评测',
  provenance: '溯源盘点',
  governance: '数据治理',
  chat: '智能客服',
  '': '角色定义（不执行）',
};

function HelpersTab() {
  const [helpers, setHelpers] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [runningId, setRunningId] = useState<number | null>(null);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ name: '', icon: '🤖', role: '', stage: '' });

  const load = useCallback(() => {
    apiFetch('/api/admin/helpers').then(r => r.json()).then(d => setHelpers(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (h: any) => {
    await apiFetch(`/api/admin/helpers/${h.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: h.enabled ? false : true }),
    });
    load();
  };

  const runHelper = async (h: any) => {
    setRunningId(h.id);
    setMsg('');
    const r = await apiFetch(`/api/admin/helpers/${h.id}/run`, { method: 'POST' });
    const d = await r.json();
    setMsg(d.message || (d.ok ? '运行完成' : '运行失败'));
    setRunningId(null);
    load();
  };

  const remove = async (h: any) => {
    if (!confirm(`确认删除助手「${h.name}」？`)) return;
    await apiFetch(`/api/admin/helpers/${h.id}`, { method: 'DELETE' });
    load();
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    await apiFetch('/api/admin/helpers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setShowAdd(false);
    setForm({ name: '', icon: '🤖', role: '', stage: '' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">AI 助手</h1>
          <p className="text-sm text-gray-500 mt-1">后台拆分为多个专职智能助手，各负责一项后台管理工作；可单独运行、启停，也可自定义新增。</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">{showAdd ? '取消' : '＋ 新增助手'}</button>
      </div>

      {msg && <div className="mb-4 px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-cyan-300">{msg}</div>}

      {showAdd && (
        <form onSubmit={add} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-6 grid grid-cols-3 gap-3">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="助手名称 *" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" required />
          <input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="图标 (emoji)" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
          <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
            <option value="">不绑定执行功能（角色定义）</option>
            <option value="crawl">数据采集（爬取发现）</option>
            <option value="news">新闻情报</option>
            <option value="probe">可用性探测</option>
            <option value="scoring">评分评测</option>
            <option value="provenance">溯源盘点</option>
            <option value="governance">数据治理</option>
            <option value="chat">智能客服</option>
          </select>
          <input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="职责说明（这个助手负责什么）" className="col-span-3 px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
          <button type="submit" className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30 w-fit">创建助手</button>
        </form>
      )}

      <div className="grid grid-cols-3 gap-4">
        {helpers.map((h: any) => (
          <div key={h.id} className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{h.icon}</div>
                <div>
                  <div className="font-semibold text-gray-100">{h.name} {h.builtin === 1 && <span className="ml-1 text-[0.6rem] text-gray-600 border border-gray-700 rounded px-1">内置</span>}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{HELPER_STAGE_LABELS[h.stage] || h.stage}</div>
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                <input type="checkbox" checked={h.enabled === 1} onChange={() => toggle(h)} className="accent-cyan-500" /> {h.enabled === 1 ? '启用' : '停用'}
              </label>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed min-h-[3.5rem]">{h.role}</p>
            <div className="text-[0.68rem] text-gray-600 min-h-[1rem]">
              {h.last_run_at ? `上次运行 ${h.last_run_at}` : '尚未运行'}
            </div>
            {h.last_result && <div className="text-[0.68rem] text-cyan-500/80 truncate" title={h.last_result}>{h.last_result}</div>}
            <div className="flex items-center gap-2 mt-auto">
              <button onClick={() => runHelper(h)} disabled={runningId === h.id} className="flex-1 px-3 py-1.5 bg-violet-500/20 text-violet-400 rounded text-sm hover:bg-violet-500/30 disabled:opacity-50">
                {runningId === h.id ? '运行中…' : '立即运行'}
              </button>
              {h.builtin !== 1 && <button onClick={() => remove(h)} className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded text-sm hover:bg-red-500/20">删除</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ initialFilter }: { initialFilter?: any }) {
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planCodeFilter, setPlanCodeFilter] = useState(initialFilter?.planCode || 'all');
  const [copied, setCopied] = useState<string | null>(null);
  const PLAN_LABELS: Record<string, string> = {
    catalog_yearly: '模型更新·年会员', catalog_lifetime: '模型更新·永久会员',
    router_monthly: '软件授权·月付', router_yearly: '软件授权·年付', router_lifetime: '软件授权·终身',
  };

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: '50' });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (planCodeFilter !== 'all') params.set('planCode', planCodeFilter);
    apiFetch(`/api/admin/orders?${params}`).then(r => r.json()).then(d => { setOrders(d.data || []); setTotal(d.total || 0); }).catch(() => {});
  }, [page, statusFilter, planCodeFilter]);

  useEffect(() => { load(); }, [load]);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleStatus = async (outTradeNo: string, status: string) => {
    await apiFetch(`/api/admin/orders/${outTradeNo}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    load();
  };

  const fmtMoney = (v: any) => Number(v || 0).toFixed(2);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">订单管理 <span className="text-sm text-gray-500">({total})</span></h1>
        <div className="flex gap-2">
          {planCodeFilter !== 'all' && <span className="px-3 py-1.5 bg-sky-500/15 text-sky-400 rounded text-sm flex items-center gap-2">产品: {planCodeFilter} <button onClick={() => { setPlanCodeFilter('all'); setPage(1); }} className="text-sky-300 hover:text-sky-200">✕</button></span>}
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
            <option value="all">全部</option>
            <option value="pending">待支付</option>
            <option value="paid">已支付</option>
            <option value="failed">已失败</option>
            <option value="refunded">已退款</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 text-left">订单号</th>
              <th className="py-2 text-left">邮箱</th>
              <th className="py-2 text-left">方案</th>
              <th className="py-2 text-right">金额</th>
              <th className="py-2 text-center">状态</th>
              <th className="py-2 text-left">KEY</th>
              <th className="py-2 text-left">创建时间</th>
              <th className="py-2 text-left">支付时间</th>
              <th className="py-2 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o: any) => (
              <tr key={o.out_trade_no} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-400 font-mono text-xs">{o.out_trade_no}</td>
                <td className="py-2 text-gray-300">{o.email}</td>
                <td className="py-2 text-gray-400">{PLAN_LABELS[o.plan_code] || o.plan_code || o.plan}</td>
                <td className="py-2 text-right text-cyan-400">¥{fmtMoney(o.amount)}</td>
                <td className="py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    o.status === 'paid' ? 'text-emerald-400 bg-emerald-500/10' :
                    o.status === 'pending' ? 'text-yellow-400 bg-yellow-500/10' :
                    o.status === 'failed' ? 'text-red-400 bg-red-500/10' :
                    'text-gray-400 bg-gray-500/10'
                  }`}>{o.status}</span>
                </td>
                <td className="py-2">
                  {o.sync_token ? (
                    <div className="flex items-center gap-1">
                      <code className="text-xs text-gray-400 font-mono truncate max-w-[120px]">{o.sync_token}</code>
                      <button onClick={() => copy(o.sync_token)} className="text-xs text-cyan-400 hover:text-cyan-300">{copied === o.sync_token ? '✓' : '复制'}</button>
                    </div>
                  ) : <span className="text-gray-600 text-xs">-</span>}
                </td>
                <td className="py-2 text-gray-500 text-xs">{o.created_at?.slice(0, 19)}</td>
                <td className="py-2 text-gray-500 text-xs">{o.paid_at?.slice(0, 19) || '-'}</td>
                <td className="py-2 text-center">
                  {o.status === 'pending' && (
                    <button onClick={() => handleStatus(o.out_trade_no, 'failed')} className="text-xs text-red-400 hover:text-red-300">关闭</button>
                  )}
                  {o.status === 'paid' && (
                    <button onClick={() => handleStatus(o.out_trade_no, 'refunded')} className="text-xs text-yellow-400 hover:text-yellow-300">退款</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orders.length === 0 && <div className="text-gray-600 mt-4">暂无订单</div>}

      {total > 50 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">上一页</button>
          <span className="text-sm text-gray-400">第 {page} 页</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">下一页</button>
        </div>
      )}
    </div>
  );
}

function CardsTab({ initialFilter }: { initialFilter?: any }) {
  const [cards, setCards] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState(initialFilter?.kind || 'all');
  const [productFilter, setProductFilter] = useState(initialFilter?.product || 'all');
  const [showGen, setShowGen] = useState(false);
  const [generated, setGenerated] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: '50' });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (kindFilter !== 'all') params.set('kind', kindFilter);
    if (productFilter !== 'all') params.set('product', productFilter);
    apiFetch(`/api/admin/cards?${params}`).then(r => r.json()).then(d => { setCards(d.data || []); setTotal(d.total || 0); }).catch(() => {});
  }, [page, statusFilter, kindFilter, productFilter]);

  useEffect(() => { load(); }, [load]);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleDisable = async (id: number) => {
    await apiFetch(`/api/admin/cards/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'disabled' }) });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确认删除该卡密？仅未使用的卡密可删除。')) return;
    await apiFetch(`/api/admin/cards/${id}`, { method: 'DELETE' });
    load();
  };

  const PLAN_LABELS: Record<string, string> = { catalog_yearly: '年会员', catalog_lifetime: '永久会员', router_monthly: '月付', router_yearly: '年付', router_lifetime: '终身' };
  const KIND_LABELS: Record<string, string> = { catalog: '模型更新卡密', router: '软件授权卡密', software: '软件授权卡密' };
  const STATUS_COLORS: Record<string, string> = {
    unused: 'text-emerald-400 bg-emerald-500/10',
    used: 'text-cyan-400 bg-cyan-500/10',
    disabled: 'text-gray-400 bg-gray-500/10',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">卡密管理 <span className="text-sm text-gray-500">({total})</span></h1>
        <button onClick={() => setShowGen(!showGen)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">{showGen ? '取消' : '生成卡密'}</button>
      </div>

      {showGen && <GenerateCardsForm onDone={(codes) => { setShowGen(false); setGenerated(codes); load(); }} />}

      {generated.length > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
          <div className="text-emerald-400 text-sm font-semibold mb-2">已生成 {generated.length} 个卡密：</div>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {generated.map((code, i) => (
              <div key={i} className="flex items-center gap-2">
                <code className="flex-1 px-3 py-1 bg-gray-900 rounded text-xs text-emerald-300 font-mono">{code}</code>
                <button onClick={() => copy(code)} className="text-xs text-cyan-400 hover:text-cyan-300">{copied === code ? '✓' : '复制'}</button>
              </div>
            ))}
          </div>
          <button onClick={() => copy(generated.join('\n'))} className="text-xs text-cyan-400 mt-2 hover:text-cyan-300">复制全部</button>
          <button onClick={() => setGenerated([])} className="text-xs text-gray-500 ml-4 hover:text-gray-300">关闭</button>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="all">全部状态</option>
          <option value="unused">未使用</option>
          <option value="used">已使用</option>
          <option value="disabled">已禁用</option>
        </select>
        <select value={kindFilter} onChange={e => { setKindFilter(e.target.value); setPage(1); }} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="all">全部类型</option>
          <option value="catalog">模型更新卡密</option>
          <option value="router">软件授权卡密</option>
          <option value="software">软件授权卡密(自定义)</option>
        </select>
        <select value={productFilter} onChange={e => { setProductFilter(e.target.value); setPage(1); }} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="all">全部产品</option>
          <option value="yearly">年付/年会员</option>
          <option value="lifetime">终身/永久</option>
          <option value="monthly">月付</option>

        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 text-left">卡密</th>
              <th className="py-2 text-left">类型</th>
              <th className="py-2 text-left">产品</th>
              <th className="py-2 text-center">时长</th>
              <th className="py-2 text-center">状态</th>
              <th className="py-2 text-left">绑定</th>
              <th className="py-2 text-left">激活时间</th>
              <th className="py-2 text-left">到期时间</th>
              <th className="py-2 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c: any) => (
              <tr key={c.id} className="border-b border-gray-800/50">
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <code className="text-xs text-gray-300 font-mono">{c.card_code}</code>
                    <button onClick={() => copy(c.card_code)} className="text-xs text-cyan-400 hover:text-cyan-300">{copied === c.card_code ? '✓' : '复制'}</button>
                  </div>
                </td>
                <td className="py-2 text-gray-400 text-xs">{KIND_LABELS[c.kind] || c.kind}</td>
                <td className="py-2 text-gray-400">{PLAN_LABELS[`${c.kind}_${c.product}`] || c.product}</td>
                <td className="py-2 text-center text-gray-400">{c.duration_days ? (c.duration_days >= 365 ? `${Math.round(c.duration_days / 365)}年` : `${c.duration_days}天`) : '终身'}</td>
                <td className="py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[c.status] || 'text-gray-400'}`}>{c.status}</span>
                </td>
                <td className="py-2 text-gray-400 text-xs">{c.bound_machine ? `设备:${String(c.bound_machine).slice(0, 12)}…` : (c.bound_email || '-')}</td>
                <td className="py-2 text-gray-500 text-xs">{c.activated_at?.slice(0, 19) || '-'}</td>
                <td className="py-2 text-gray-500 text-xs">{c.expires_at ? (c.expires_at === 'lifetime' ? '永久/终身' : c.expires_at.slice(0, 10)) : (c.duration_days ? '-' : '永久/终身')}</td>
                <td className="py-2 text-center">
                  {c.status === 'unused' && (
                    <>
                      <button onClick={() => handleDisable(c.id)} className="text-xs text-yellow-400 hover:text-yellow-300 mr-2">禁用</button>
                      <button onClick={() => handleDelete(c.id)} className="text-xs text-red-400 hover:text-red-300">删除</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cards.length === 0 && <div className="text-gray-600 mt-4">暂无卡密</div>}

      {total > 50 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">上一页</button>
          <span className="text-sm text-gray-400">第 {page} 页</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-3 py-1 bg-gray-800 rounded text-sm disabled:opacity-30">下一页</button>
        </div>
      )}
    </div>
  );
}

/** 生成卡密：两种类型（模型更新卡密 / 软件授权卡密），套餐联动时长 */
function GenerateCardsForm({ onDone }: { onDone: (codes: string[]) => void }) {
  const [plans, setPlans] = useState<any[]>([]);
  const [planCode, setPlanCode] = useState('');
  const [form, setForm] = useState({ count: '10', note: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/admin/plans').then(r => r.json()).then((ps: any[]) => {
      const enabled = ps.filter((p: any) => p.enabled);
      setPlans(enabled);
      if (enabled[0]) setPlanCode(enabled[0].code);
    }).catch(() => {});
  }, []);

  const selected = plans.find(p => p.code === planCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!planCode) { setError('请选择产品'); return; }
    const r = await apiFetch('/api/admin/cards/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planCode, count: parseInt(form.count) || 1, note: form.note }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '生成失败'); return; }
    onDone(d.cards || []);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4 space-y-3">
      <div className="grid grid-cols-4 gap-3 items-end">
        <select value={planCode} onChange={e => setPlanCode(e.target.value)} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          {plans.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
        <input value={form.count} onChange={e => setForm({ ...form, count: e.target.value })} placeholder="数量" type="number" min="1" max="500" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
        <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="备注（选填）" className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
        <button type="submit" className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">生成</button>
      </div>
      {selected && <div className="text-[11px] text-gray-500">{selected.kind === 'catalog' ? '模型更新卡密（CXAI- 开头）：在网站兑换绑定邮箱' : '软件授权卡密（CXAR- 开头）：一卡一机，客户端软件传 machineId 激活'}。时长 {selected.duration_days > 0 ? `${selected.duration_days} 天` : '永久'}。</div>}
      {error && <span className="text-xs text-red-400">{error}</span>}
    </form>
  );
}

function SubsTab({ initialFilter }: { initialFilter?: any }) {
  const [subs, setSubs] = useState<any[]>([]);
  const [planFilter, setPlanFilter] = useState(initialFilter?.plan || 'all');
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(() => {
    apiFetch('/api/admin/subscriptions').then(r => r.json()).then(setSubs).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = planFilter === 'all' ? subs : subs.filter((s: any) => s.plan === planFilter || s.plan_code === planFilter);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleStatus = async (id: number, status: string) => {
    await apiFetch(`/api/admin/subscriptions/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确认删除该订阅？KEY 将立即失效。')) return;
    await apiFetch(`/api/admin/subscriptions/${id}`, { method: 'DELETE' });
    load();
  };

  const handleRegen = async (id: number) => {
    if (!confirm('确认重新生成 KEY？旧 KEY 将立即失效。')) return;
    const r = await apiFetch(`/api/admin/subscriptions/${id}/regenerate`, { method: 'POST' });
    const d = await r.json();
    setNewKey(d.subscription?.sync_token || null);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">订阅管理 <span className="text-sm text-gray-500">({filtered.length})</span></h1>
        <div className="flex gap-2">
          {planFilter !== 'all' && <span className="px-3 py-1.5 bg-sky-500/15 text-sky-400 rounded text-sm flex items-center gap-2">产品: {planFilter} <button onClick={() => setPlanFilter('all')} className="text-sky-300 hover:text-sky-200">✕</button></span>}
          <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">{showAdd ? '取消' : '手工生成 KEY'}</button>
        </div>
      </div>

      {showAdd && <AddSubForm onDone={(key) => { setShowAdd(false); setNewKey(key); load(); }} />}

      {newKey && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
          <div className="text-emerald-400 text-sm font-semibold mb-2">新 KEY 已生成，请复制并分发给用户：</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-gray-900 rounded text-xs text-emerald-300 font-mono break-all">{newKey}</code>
            <button onClick={() => copy(newKey)} className="px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded text-xs hover:bg-emerald-500/30">{copied === newKey ? '已复制' : '复制'}</button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-xs text-gray-500 mt-2 hover:text-gray-300">关闭</button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 text-left">ID</th>
              <th className="py-2 text-left">邮箱</th>
              <th className="py-2 text-left">方案</th>
              <th className="py-2 text-center">状态</th>
              <th className="py-2 text-left">KEY</th>
              <th className="py-2 text-left">到期</th>
              <th className="py-2 text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s: any) => (
              <tr key={s.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-500">{s.id}</td>
                <td className="py-2 text-gray-300">{s.email}</td>
                <td className="py-2 text-gray-400">{s.plan}</td>
                <td className="py-2 text-center"><span className={`text-xs ${s.status === 'active' ? 'text-emerald-400' : 'text-gray-400'}`}>{s.status}</span></td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <code className="text-xs text-gray-400 font-mono truncate max-w-[160px]">{s.sync_token}</code>
                    <button onClick={() => copy(s.sync_token)} className="text-xs text-cyan-400 hover:text-cyan-300">{copied === s.sync_token ? '✓' : '复制'}</button>
                  </div>
                </td>
                <td className="py-2 text-gray-500 text-xs">{s.expires_at?.slice(0, 10) || '永久'}</td>
                <td className="py-2 text-center whitespace-nowrap">
                  {s.status === 'active'
                    ? <button onClick={() => handleStatus(s.id, 'cancelled')} className="text-xs text-yellow-400 hover:text-yellow-300 mr-2">停用</button>
                    : <button onClick={() => handleStatus(s.id, 'active')} className="text-xs text-emerald-400 hover:text-emerald-300 mr-2">启用</button>}
                  <button onClick={() => handleRegen(s.id)} className="text-xs text-cyan-400 hover:text-cyan-300 mr-2">换Key</button>
                  <button onClick={() => handleDelete(s.id)} className="text-xs text-red-400 hover:text-red-300">删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {subs.length === 0 && <div className="text-gray-600 mt-4">暂无订阅</div>}
    </div>
  );
}

function AddSubForm({ onDone }: { onDone: (key: string) => void }) {
  const [form, setForm] = useState({ email: '', plan: 'annual' });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const r = await apiFetch('/api/admin/subscriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '创建失败'); return; }
    onDone(d.subscription?.sync_token || '');
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4 flex gap-3 items-end">
      <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="用户邮箱 *" type="email" className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" required />
      <select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
        <option value="annual">年付 (365天)</option>
        <option value="lifetime">终身</option>
        <option value="free">免费</option>
      </select>
      <button type="submit" className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">生成 KEY</button>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </form>
  );
}
function SettingsTab() {
  const [settings, setSettings] = useState<any>(null);
  const [form, setForm] = useState({ adminPort: '', adminEntry: '', adminAccessCode: '', email: '', password: '', adminBindHost: '', adminAllowedIps: '', adminAllowedMacs: '', tunnelMode: '', tunnelServer: '', tunnelToken: '', syncToken: '', syncPeers: '[]', siteFooter: '', loginTitle: '' });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('panel');

  const load = useCallback(() => {
    apiFetch('/api/admin/settings').then(r => r.json()).then(d => {
      setSettings(d);
      setForm({
        adminPort: d.adminPort || '', adminEntry: d.adminEntry || '', adminAccessCode: d.adminAccessCode || '', email: d.adminEmail || '', password: '',
        adminBindHost: d.adminBindHost || '', adminAllowedIps: d.adminAllowedIps || '', adminAllowedMacs: d.adminAllowedMacs || '',
        tunnelMode: d.tunnelMode || 'off', tunnelServer: d.tunnelServer || '', tunnelToken: d.tunnelToken || '',
        syncToken: d.syncToken || '', syncPeers: d.syncPeers || '[]',
        siteFooter: d.siteFooter || '', loginTitle: d.loginTitle || '',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    setMsg(''); setError('');
    const r = await apiFetch('/api/admin/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        adminPort: form.adminPort, adminEntry: form.adminEntry, adminAccessCode: form.adminAccessCode,
        adminBindHost: form.adminBindHost, adminAllowedIps: form.adminAllowedIps, adminAllowedMacs: form.adminAllowedMacs,
        tunnelMode: form.tunnelMode, tunnelServer: form.tunnelServer, tunnelToken: form.tunnelToken,
        syncToken: form.syncToken, syncPeers: form.syncPeers,
        siteFooter: form.siteFooter, loginTitle: form.loginTitle,
      }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '保存失败'); return; }
    setMsg(d.message || '已保存');
  };

  const handleSaveAccount = async () => {
    setMsg(''); setError('');
    const body: any = {};
    if (form.email && form.email !== settings?.adminEmail) body.email = form.email;
    if (form.password) body.password = form.password;
    if (!body.email && !body.password) { setError('未修改任何字段'); return; }
    const r = await apiFetch('/api/admin/account', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '保存失败'); return; }
    setMsg(d.message || '账号已更新');
    setForm({ ...form, password: '' });
    load();
  };

  const handleRestart = async () => {
    if (!confirm('确认重启服务？将短暂中断所有连接。')) return;
    setMsg('正在重启...');
    await apiFetch('/api/admin/restart', { method: 'POST' });
  };

  const SETTING_TABS = [
    { key: 'panel', icon: Shield, title: '面板与安全入口', desc: '端口 · 入口 · 登录码' },
    { key: 'content', icon: FileText, title: '站点内容', desc: '页脚 · 登录页标题' },
    { key: 'network', icon: Network, title: '网络访问控制', desc: '绑定 IP · 白名单 · MAC' },
    { key: 'tunnel', icon: Globe, title: '内网穿透', desc: 'frp / Cloudflare' },
    { key: 'sync', icon: ServerCog, title: '双机对等同步', desc: 'PeerSync 冗余' },
    { key: 'account', icon: UserCog, title: '管理账号', desc: '邮箱 · 密码' },
    { key: 'restart', icon: Power, title: '重启服务', desc: '重启生效' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold">系统设置</h1>
        <span className="text-xs text-gray-500">修改端口/入口等参数后需重启服务生效</span>
      </div>

      {/* 卡片式选项卡 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {SETTING_TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setMsg(''); setError(''); }}
              className={`text-left rounded-xl border p-3.5 transition-all ${active ? 'border-cyan-500 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.15)]' : 'border-gray-700/60 bg-gray-900/40 hover:border-gray-500 hover:bg-gray-800/60'}`}>
              <div className={`flex h-8 w-8 items-center justify-center rounded-lg mb-2 ${active ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-800 text-gray-400'}`}>
                <Icon size={16} />
              </div>
              <div className={`text-sm font-medium ${active ? 'text-cyan-300' : 'text-gray-300'}`}>{t.title}</div>
              <div className="text-[11px] text-gray-500 mt-0.5 truncate">{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* 当前选项卡内容 */}
      <div className="max-w-2xl">
        {activeTab === 'panel' && (
          <SettingsCard icon={Shield} title="面板与安全入口" desc="管理端口 · 安全入口路径 · 登录码" action={<SaveBtn onClick={handleSaveSettings} />}>
            <Field label="管理端口" hint="后台服务监听端口">
              <input value={form.adminPort} onChange={e => setForm({ ...form, adminPort: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="3019" />
            </Field>
            <Field label="安全入口路径" hint="访问 http://端口/<入口>/login 进入后台；修改后需与前端 VITE_ADMIN_ENTRY 一致并重新构建">
              <input value={form.adminEntry} onChange={e => setForm({ ...form, adminEntry: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="admin" />
            </Field>
            <Field label="安全登录码" hint="登录时额外校验；留空则不校验">
              <input value={form.adminAccessCode} onChange={e => setForm({ ...form, adminAccessCode: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="留空则不校验" />
            </Field>
          </SettingsCard>
        )}

        {activeTab === 'content' && (
          <SettingsCard icon={FileText} title="站点内容" desc="全站页脚文案 · 登录页标题（页面其余内容请在用「页面设计」编辑）" action={<SaveBtn onClick={handleSaveSettings} />}>
            <Field label="站点页脚文案" hint="显示在所有公开页底部">
              <input value={form.siteFooter} onChange={e => setForm({ ...form, siteFooter: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="CXAI-FreeRouter · 免费模型目录服务" />
            </Field>
            <Field label="登录页标题" hint="管理后台登录页显示的标题">
              <input value={form.loginTitle} onChange={e => setForm({ ...form, loginTitle: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="管理后台登录" />
            </Field>
          </SettingsCard>
        )}

        {activeTab === 'network' && (
          <SettingsCard icon={Network} title="网络访问控制" desc="后台绑定 IP · 来源白名单 · MAC 绑定（重启后生效）" action={<SaveBtn onClick={handleSaveSettings} />}>
            <Field label="后台监听地址（绑定 IP）" hint="默认仅本机；填 0.0.0.0 或局域网 IP 可让其他机器访问">
              <input value={form.adminBindHost} onChange={e => setForm({ ...form, adminBindHost: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="127.0.0.1" />
            </Field>
            <Field label="允许来源 IP 白名单" hint="逗号分隔：单 IP / CIDR / lan / all；留空=仅本机；反代部署需 TRUST_PROXY=1">
              <input value={form.adminAllowedIps} onChange={e => setForm({ ...form, adminAllowedIps: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="192.168.1.100,192.168.1.0/24 或 lan" />
            </Field>
            <Field label="MAC 白名单（局域网绑定登录，可选）" hint="仅同局域网可解析 MAC；跨网段/公网无法解析将拒绝访问">
              <input value={form.adminAllowedMacs} onChange={e => setForm({ ...form, adminAllowedMacs: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="AA-BB-CC-DD-EE-FF,11-22-33-44-55-66" />
            </Field>
          </SettingsCard>
        )}

        {activeTab === 'tunnel' && (
          <SettingsCard icon={Globe} title="内网穿透" desc="frp / Cloudflare 隧道，无公网 IP 也能对外提供服务（重启后生效）" action={<SaveBtn onClick={handleSaveSettings} />}>
            <Field label="模式">
              <div className="grid grid-cols-3 gap-2">
                {(['off', 'frp', 'cloudflared'] as const).map(t => (
                  <button key={t} onClick={() => setForm({ ...form, tunnelMode: t })}
                    className={`px-3 py-2 rounded text-sm border ${form.tunnelMode === t ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' : 'border-gray-800 text-gray-400 hover:border-gray-600'}`}>
                    {t === 'off' ? '关闭' : t === 'frp' ? 'frp' : 'Cloudflare'}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={form.tunnelMode === 'frp' ? 'frps 服务器地址' : form.tunnelMode === 'cloudflared' ? '公网域名（可选）' : '隧道参数'}>
              <input value={form.tunnelServer} onChange={e => setForm({ ...form, tunnelServer: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder={form.tunnelMode === 'frp' ? 'vps.example.com:7000' : 'example.com'} />
            </Field>
            {form.tunnelMode === 'frp' && (
              <Field label="frp 认证 token">
                <input value={form.tunnelToken} onChange={e => setForm({ ...form, tunnelToken: e.target.value })} type="password" className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
              </Field>
            )}
            <p className="text-xs text-gray-500">配置与启动脚本由安装向导生成于 deploy/tunnel/；也可手动运行 frpc / cloudflared</p>
          </SettingsCard>
        )}

        {activeTab === 'sync' && (
          <SettingsCard icon={ServerCog} title="双机对等同步" desc="PeerSync 双向增量同步 · 目录类数据自动收敛（60s 一轮）" action={<SaveBtn onClick={handleSaveSettings} />}>
            <Field label="对等同步令牌（两台机器填相同值）" hint="同步端点 /api/sync/peer 以该令牌鉴权">
              <input value={form.syncToken} onChange={e => setForm({ ...form, syncToken: e.target.value })} type="password" className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="与对端一致的安全令牌" />
            </Field>
            <Field label="对等节点列表（JSON）" hint="每台机器填对方地址；订单/卡密/订阅不参与双向（防双花）">
              <textarea value={form.syncPeers} onChange={e => setForm({ ...form, syncPeers: e.target.value })} rows={3}
                className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm font-mono" placeholder='[{"id":"node-b","url":"http://192.168.1.50:3018","enabled":true}]' />
            </Field>
          </SettingsCard>
        )}

        {activeTab === 'account' && (
          <SettingsCard icon={UserCog} title="管理账号" desc="登录邮箱与密码（修改密码后旧会话全部失效）" action={<SaveBtn onClick={handleSaveAccount} label="保存账号" />}>
            <Field label="登录邮箱">
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" />
            </Field>
            <Field label="新密码（留空不修改）">
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm" placeholder="••••••••" />
            </Field>
          </SettingsCard>
        )}

        {activeTab === 'restart' && (
          <SettingsCard icon={Power} title="重启服务" desc="端口/入口/绑定地址等修改需重启生效；将短暂中断所有连接" action={
            <button onClick={handleRestart} className="px-4 py-1.5 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30">重启服务</button>
          }>
            <p className="text-sm text-gray-500 leading-relaxed">重启后需进程管理器（PM2 / systemd / Supervisor）自动拉起；当前进程由本机启动脚本托管。</p>
          </SettingsCard>
        )}
      </div>

      {msg && <div className="text-sm text-emerald-400 mt-4">{msg}</div>}
      {error && <div className="text-sm text-red-400 mt-4">{error}</div>}
    </div>
  );
}

/** 设置卡片容器：图标 + 标题 + 描述 + 内容 + 底部操作 */
function SettingsCard({ icon: Icon, title, desc, children, action }: { icon: LucideIcon; title: string; desc: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-700/60 bg-gray-900/40 p-5 flex flex-col gap-4 transition-colors hover:border-cyan-500/40">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-cyan-500/20 bg-cyan-500/10 text-cyan-400">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-100">{title}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="space-y-3 flex-1">{children}</div>
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm text-gray-400 block mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function SaveBtn({ onClick, label = '保存设置' }: { onClick: () => void; label?: string }) {
  return <button onClick={onClick} className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">{label}</button>;
}

/* =====================================================================
 * 数据导入：厂商 + 模型（含 URL 地址与额度）
 * 支持：粘贴 JSON / 上传 JSON、CSV / 远程 URL 拉取（后端 safeFetch 防 SSRF）
 * ===================================================================== */
const VENDOR_CSV_HEADERS = ['id', 'name', 'base_url', 'website_url', 'key_url', 'requires_key', 'free_tier_info', 'region', 'category', 'description', 'status'];
const MODEL_CSV_HEADERS = ['platform', 'model_id', 'display_name', 'category', 'context_window', 'free_quota', 'free_quota_label', 'reset_cycle', 'size_label', 'supports_vision', 'supports_tools', 'input_price', 'output_price'];
const VENDOR_JSON_SAMPLE = `{
  "vendors": [
    { "id": "openrouter", "name": "OpenRouter", "base_url": "https://openrouter.ai/api/v1",
      "website_url": "https://openrouter.ai", "key_url": "https://openrouter.ai/settings/keys",
      "requires_key": 1, "free_tier_info": "每日 1000 次免费请求，限 :free 模型", "category": "chat" }
  ],
  "models": [
    { "platform": "openrouter", "model_id": "qwen/qwen2.5-7b-instruct:free",
      "display_name": "Qwen 2.5 7B (Free)", "category": "chat",
      "context_window": 32768, "free_quota": 100000, "free_quota_label": "100k/天",
      "reset_cycle": "daily", "supports_tools": 1, "supports_vision": 0 }
  ]
}`;
const VENDOR_CSV_SAMPLE = `id,name,base_url,website_url,key_url,requires_key,free_tier_info
openrouter,OpenRouter,https://openrouter.ai/api/v1,https://openrouter.ai,https://openrouter.ai/settings/keys,1,每日1000次免费`;
const MODEL_CSV_SAMPLE = `platform,model_id,display_name,category,context_window,free_quota,free_quota_label,reset_cycle,supports_vision,supports_tools
openrouter,qwen/qwen2.5-7b-instruct:free,Qwen 2.5 7B Free,chat,32768,100000,100k/天,daily,0,1`;

function parseCsv(text: string): any[] {
  const rows: string[][] = [];
  let cur = '', inQ = false;
  const row: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
      else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cur); cur = '';
      if (row.some(x => x.trim() !== '')) rows.push(row);
      row.length = 0;
    } else cur += c;
  }
  row.push(cur);
  if (row.some(x => x.trim() !== '')) rows.push(row);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => h.trim());
  return rows.slice(1).map(r => {
    const o: any = {};
    headers.forEach((h, idx) => { o[h] = (r[idx] ?? '').trim(); });
    return o;
  });
}

function ImportTab() {
  const [mode, setMode] = useState<'json' | 'file' | 'url'>('json');
  const [text, setText] = useState('');
  const [fileErr, setFileErr] = useState('');
  const [fileInfo, setFileInfo] = useState<{ name: string; vendors: any[]; models: any[] } | null>(null);
  const [url, setUrl] = useState('');
  const [pending, setPending] = useState<{ vendors: any[]; models: any[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const handleParse = () => {
    setError(''); setResult(null);
    try {
      const data = JSON.parse(text);
      const vendors = Array.isArray(data.vendors) ? data.vendors : [];
      const models = Array.isArray(data.models) ? data.models : [];
      if (!vendors.length && !models.length) { setError('JSON 中需包含 vendors 或 models 数组'); return; }
      setPending({ vendors, models });
    } catch (e: any) { setError('JSON 解析失败：' + e.message); }
  };

  const handleFile = (f: File | undefined) => {
    setError(''); setResult(null); setFileInfo(null); setPending(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const textStr = String(reader.result || '');
      try {
        if (/\.json$/i.test(f.name)) {
          const data = JSON.parse(textStr);
          const vendors = Array.isArray(data.vendors) ? data.vendors : [];
          const models = Array.isArray(data.models) ? data.models : [];
          if (!vendors.length && !models.length) { setFileErr('JSON 中需包含 vendors 或 models 数组'); return; }
          setFileInfo({ name: f.name, vendors, models });
          setPending({ vendors, models });
        } else if (/\.csv$/i.test(f.name)) {
          const rows = parseCsv(textStr);
          const vendors = rows.filter(r => r.id && r.name && r.base_url);
          const models = rows.filter(r => r.platform && r.model_id);
          setFileInfo({ name: f.name, vendors, models });
          setPending({ vendors, models });
        } else { setFileErr('仅支持 .json / .csv 文件'); }
      } catch (e: any) { setFileErr('文件解析失败：' + e.message); }
    };
    reader.readAsText(f);
  };

  const handleUrl = async () => {
    setError(''); setResult(null); setBusy(true);
    try {
      const r = await apiFetch('/api/admin/import/url', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: url.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || '拉取失败'); return; }
      setResult(d);
    } catch (e: any) { setError('请求失败：' + e.message); }
    finally { setBusy(false); }
  };

  const handleImport = async () => {
    if (!pending) return;
    setError(''); setResult(null); setBusy(true);
    try {
      const r = await apiFetch('/api/admin/import', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pending),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || '导入失败'); return; }
      setResult(d);
    } catch (e: any) { setError('请求失败：' + e.message); }
    finally { setBusy(false); }
  };

  const count = pending ? (pending.vendors.length + pending.models.length) : 0;
  const resultBlock = (label: string, stat: any) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-emerald-400">新增 {stat?.inserted ?? 0}</span>
      <span className="text-cyan-400">更新 {stat?.updated ?? 0}</span>
      <span className="text-yellow-400/80">跳过 {stat?.skipped ?? 0}</span>
      {!!stat?.errors?.length && <span className="text-red-400">错误 {stat.errors.length}</span>}
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">数据导入</h1>
      <p className="text-sm text-gray-500 mb-6">批量导入厂商与模型数据（含 API Base URL、Key 注册地址与免费额度）。按业务键合并：厂商=id、模型=platform+model_id；已存在则更新，未知字段自动丢弃。</p>

      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-300 mb-2">格式模板</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="bg-gray-950 border border-gray-800 rounded p-3">
            <div className="text-cyan-400 mb-2">JSON（厂商 + 模型，URL 与额度字段见下）</div>
            <pre className="whitespace-pre-wrap break-all text-gray-400 max-h-56 overflow-auto">{VENDOR_JSON_SAMPLE}</pre>
          </div>
          <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded p-3">
              <div className="text-cyan-400 mb-2">厂商 CSV 表头</div>
              <pre className="whitespace-pre-wrap break-all text-gray-400">{VENDOR_CSV_SAMPLE}</pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded p-3">
              <div className="text-cyan-400 mb-2">模型 CSV 表头</div>
              <pre className="whitespace-pre-wrap break-all text-gray-400">{MODEL_CSV_SAMPLE}</pre>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-3 gap-2 mb-4">
          {([
            ['json', '粘贴 JSON'],
            ['file', '上传文件'],
            ['url', 'URL 拉取'],
          ] as [string, string][]).map(([m, label]) => (
            <button key={m} onClick={() => { setMode(m as any); setError(''); setResult(null); }}
              className={`px-3 py-2 rounded text-sm border ${mode === m ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' : 'border-gray-800 text-gray-400 hover:border-gray-600'}`}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'json' && (
          <div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={8}
              placeholder='粘贴 JSON，例如 {"vendors":[...], "models":[...]}'
              className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm font-mono text-gray-300" />
            <button onClick={handleParse} className="mt-2 px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">解析预览</button>
            <button onClick={() => setText(VENDOR_JSON_SAMPLE)} className="mt-2 ml-2 px-4 py-1.5 bg-gray-800 text-gray-400 rounded text-sm hover:bg-gray-700">填充示例</button>
          </div>
        )}
        {mode === 'file' && (
          <div>
            <input type="file" accept=".json,.csv" onChange={e => handleFile(e.target.files?.[0])}
              className="text-sm text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-cyan-500/20 file:text-cyan-400" />
            {fileErr && <div className="text-sm text-red-400 mt-2">{fileErr}</div>}
            {fileInfo && (
              <div className="text-xs text-gray-400 mt-2">文件 <span className="text-cyan-400">{fileInfo.name}</span>：厂商 {fileInfo.vendors.length} 条、模型 {fileInfo.models.length} 条（CSV 需表头 id,name,base_url / platform,model_id）</div>
            )}
          </div>
        )}
        {mode === 'url' && (
          <div>
            <div className="flex gap-2">
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/models.json（返回 {vendors:[...],models:[...]}）"
                className="flex-1 px-3 py-2 bg-gray-950 border border-gray-800 rounded text-sm font-mono text-gray-300" />
              <button onClick={handleUrl} disabled={busy || !url.trim()} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30 disabled:opacity-40">拉取并导入</button>
            </div>
            <p className="text-xs text-gray-500 mt-1">后端经 SSRF 防护抓取（仅 http/https、拦截私网/环回/组播、≤2MB），仅接受合法 JSON</p>
          </div>
        )}
      </div>

      {pending && (
        <div className="bg-gray-900/50 border border-emerald-500/30 rounded-lg p-4 mb-4">
          <div className="text-sm text-gray-300 mb-2">解析预览：厂商 <span className="text-cyan-400">{pending.vendors.length}</span> 条 · 模型 <span className="text-cyan-400">{pending.models.length}</span> 条 · 共 {count} 条</div>
          <button onClick={handleImport} disabled={busy} className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded text-sm hover:bg-emerald-500/30 disabled:opacity-40">{busy ? '导入中...' : '确认导入'}</button>
          <button onClick={() => setPending(null)} className="ml-2 px-4 py-2 text-gray-500 text-sm hover:text-gray-300">取消</button>
        </div>
      )}

      {result && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-300 mb-2">导入结果{result.sourceUrl ? `（来源：${result.sourceUrl}）` : ''}</div>
          <div className="space-y-1 mb-2">
            <div className="flex items-center gap-3">厂商：{resultBlock('vendors', result.vendors)}</div>
            <div className="flex items-center gap-3">模型：{resultBlock('models', result.models)}</div>
          </div>
          {result.vendors?.errors?.length > 0 && (
            <div className="text-xs text-red-400/80 bg-red-500/5 border border-red-500/20 rounded p-2 max-h-32 overflow-auto">
              <div className="font-semibold mb-1">厂商跳过原因（前 20 条）</div>
              {result.vendors.errors.map((e: string, i: number) => <div key={i}>• {e}</div>)}
            </div>
          )}
          {result.models?.errors?.length > 0 && (
            <div className="text-xs text-red-400/80 bg-red-500/5 border border-red-500/20 rounded p-2 max-h-32 overflow-auto">
              <div className="font-semibold mb-1">模型跳过原因（前 20 条）</div>
              {result.models.errors.map((e: string, i: number) => <div key={i}>• {e}</div>)}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-3">导入写入 sync_ts，双机 PeerSync 将自动同步到对端主机。</p>
        </div>
      )}

      {error && <div className="text-sm text-red-400 mt-3">{error}</div>}
    </div>
  );
}

function PaymentTab() {
  const [cfg, setCfg] = useState<any>(null);
  const [form, setForm] = useState({ appId: '', mchId: '', apiV3Key: '', certSerial: '', notifyUrl: '', privateKey: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => {
    apiFetch('/api/admin/payment/config').then(r => r.json()).then(d => setCfg(d)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const resp = await apiFetch('/api/admin/payment/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: form.appId, mchId: form.mchId, apiV3Key: form.apiV3Key,
          certSerial: form.certSerial, notifyUrl: form.notifyUrl, privateKey: form.privateKey,
        }),
      });
      const d = await resp.json();
      if (!resp.ok) { setMsg({ ok: false, text: d.error || '保存失败' }); return; }
      setMsg({ ok: d.enabled, text: d.message });
      setForm({ appId: '', mchId: '', apiV3Key: '', certSerial: '', notifyUrl: '', privateKey: '' });
      load();
    } catch {
      setMsg({ ok: false, text: '网络错误' });
    } finally {
      setSaving(false);
    }
  };

  const set = (k: string) => (e: any) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">支付设置 <span className="text-sm text-gray-500">微信支付 Native 扫码收款</span></h1>
      <p className="text-xs text-gray-500 mb-6">配置保存在本机数据库，无需重启进程即可生效；也支持 WECHAT_* 环境变量（优先级更高）。</p>

      {cfg && (
        <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${cfg.enabled ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300' : 'border-red-500/30 bg-red-500/5 text-red-300'}`}>
          {cfg.enabled ? '● 微信支付已启用，公开页扫码下单可用' : '● 微信支付未启用（缺少以下必填项），公开页购买按钮暂不可用'}
          {!cfg.enabled && cfg.missing?.length > 0 && (
            <div className="mt-2 text-xs text-red-400/80">{cfg.missing.map((m: any) => <div key={m.key}>· {m.label}</div>)}</div>
          )}
        </div>
      )}

      {cfg && (
        <div className="grid grid-cols-2 gap-2 mb-6 max-w-2xl">
          {cfg.fields?.map((f: any) => (
            <div key={f.key} className="flex items-center justify-between rounded border border-gray-700 px-3 py-2 text-xs">
              <span className="text-gray-400">{f.label}</span>
              <span className={f.configured ? 'text-emerald-400' : 'text-red-400'}>
                {f.configured ? (f.source === 'env' ? '已配置(环境变量)' : '已配置') : '未配置'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="max-w-2xl space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">小程序/公众号 AppID</label>
          <input value={form.appId} onChange={set('appId')} placeholder="wx1234567890abcdef" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">商户号 MchID</label>
          <input value={form.mchId} onChange={set('mchId')} placeholder="1900000109" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">APIv3 密钥（32 位）</label>
          <input value={form.apiV3Key} onChange={set('apiV3Key')} placeholder="在微信商户平台 → API 安全 → APIv3 密钥 设置" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">商户证书序列号</label>
          <input value={form.certSerial} onChange={set('certSerial')} placeholder="商户平台 → API 安全 → 证书 查看序列号" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">支付回调 URL（须公网 HTTPS）</label>
          <input value={form.notifyUrl} onChange={set('notifyUrl')} placeholder="https://你的域名/api/payment/notify" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
          <p className="text-xs text-gray-500 mt-1">{cfg?.notifyHint}</p>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">商户 API 私钥（PEM 全文）</label>
          <textarea value={form.privateKey} onChange={set('privateKey')} rows={5} placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----" className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm font-mono outline-none focus:border-cyan-500" />
          <p className="text-xs text-gray-500 mt-1">微信商户平台 → API 安全 → 申请 API 证书时下载的 apiclient_key.pem 内容。私钥仅保存在本机数据库，不回显。</p>
        </div>

        <button onClick={save} disabled={saving} className="rounded-lg bg-cyan-500/20 px-5 py-2.5 text-sm text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-50">
          {saving ? '保存中…' : '保存并启用'}
        </button>

        {msg && (
          <div className={`text-sm ${msg.ok ? 'text-emerald-400' : 'text-red-400'}`}>{msg.text}</div>
        )}
      </div>

      <div className="mt-8 max-w-2xl rounded-lg border border-gray-700 bg-gray-900/50 p-4 text-xs text-gray-400 leading-relaxed">
        <div className="font-semibold text-gray-300 mb-2">收款开通指引</div>
        <div>1. 在 <span className="text-cyan-300">微信商户平台（pay.weixin.qq.com）</span> 完成企业/个体户主体入驻，获得商户号；</div>
        <div>2. 在「产品中心」开通 Native 支付；</div>
        <div>3. 按上表获取 AppID / 商户号 / APIv3 密钥 / 证书序列号 / 商户私钥；</div>
        <div>4. 将回调地址配置为公网 HTTPS 域名（含 /api/payment/notify），并在商户平台「API 安全 → 回调配置」中登记；</div>
        <div>5. 填表保存后，公开页「定价方案」即恢复微信扫码购买。</div>
        <div className="mt-3 text-yellow-300/80">提示：商户号尚未办妥前，可用「卡密管理」生成兑换码线下收款，客户在公开页「定价方案 → 兑换码激活」自助开通。</div>
      </div>

      <PaymentChannelSection
        title="支付宝 · 当面付" hint="支付宝扫码支付（alipay.trade.precreate），配置应用 AppID / 私钥 / 公钥 / 回调 URL。"
        endpoint="/api/admin/payment/config-alipay"
        fields={[
          { key: 'appId', label: '应用 AppID' },
          { key: 'privateKey', label: '应用私钥（PEM 或 base64）', secret: true, textarea: true },
          { key: 'publicKey', label: '支付宝公钥（PEM 或 base64）', secret: true, textarea: true },
          { key: 'notifyUrl', label: '异步回调 URL（公网可达，路径 /api/payment/notify-alipay）' },
          { key: 'sandbox', label: '沙箱模式', checkbox: true },
        ]}
      />

      <PaymentChannelSection
        title="Stripe · 卡/Apple Pay" hint="Stripe Checkout 跳转支付，支持卡/Apple Pay/Google Pay。需配置 PUBLIC_URL 环境变量。"
        endpoint="/api/admin/payment/config-stripe"
        fields={[
          { key: 'secretKey', label: 'Secret Key (sk_live_...)', secret: true },
          { key: 'webhookSecret', label: 'Webhook Signing Secret (whsec_...)', secret: true },
        ]}
      />

      <PaymentChannelSection
        title="SMTP 邮件 · 发卡通知" hint="支付成功后自动发卡密到用户邮箱。依赖 nodemailer（需 npm install nodemailer），未安装时邮件功能静默禁用。"
        endpoint="/api/admin/payment/config-smtp"
        fields={[
          { key: 'host', label: 'SMTP 服务器' },
          { key: 'port', label: '端口' },
          { key: 'user', label: '用户名' },
          { key: 'pass', label: '密码', secret: true },
          { key: 'from', label: '发件人地址' },
          { key: 'secure', label: 'SSL/TLS', checkbox: true },
        ]}
      />
    </div>
  );
}

/** 通用支付渠道配置区块（支付宝/Stripe/SMTP 复用） */
function PaymentChannelSection({ title, hint, endpoint, fields }: { title: string; hint: string; endpoint: string; fields: Array<{ key: string; label: string; secret?: boolean; textarea?: boolean; checkbox?: boolean }> }) {
  const [cfg, setCfg] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { apiFetch(endpoint).then(r => r.json()).then(setCfg).catch(() => {}); }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const resp = await apiFetch(endpoint, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await resp.json();
      if (!resp.ok) { setMsg({ ok: false, text: d.error || '保存失败' }); return; }
      setMsg({ ok: d.enabled, text: d.enabled ? '已启用' : '已保存（仍有必填项缺失）' });
      setForm({}); load();
    } catch { setMsg({ ok: false, text: '网络错误' }); } finally { setSaving(false); }
  };

  return (
    <div className="mt-8 max-w-2xl">
      <h2 className="text-lg font-semibold mb-1">{title}</h2>
      <p className="text-xs text-gray-500 mb-4">{hint}</p>
      {cfg && (
        <div className={`mb-4 rounded-lg border px-4 py-2 text-sm ${cfg.enabled ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300' : 'border-gray-700 bg-gray-900/50 text-gray-400'}`}>
          {cfg.enabled ? '● 已启用' : '○ 未启用'}
          {cfg.fields && <span className="ml-3 text-xs text-gray-500">{cfg.fields.filter((f: any) => f.configured).length}/{cfg.fields.length} 项已配置</span>}
        </div>
      )}
      <div className="space-y-3">
        {fields.map(f => (
          <div key={f.key}>
            <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
            {f.checkbox ? (
              <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} className="accent-cyan-500" />
            ) : f.textarea ? (
              <textarea value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} rows={4} className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm font-mono outline-none focus:border-cyan-500" />
            ) : (
              <input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm outline-none focus:border-cyan-500" />
            )}
          </div>
        ))}
        <button onClick={save} disabled={saving} className="rounded-lg bg-cyan-500/20 px-5 py-2.5 text-sm text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-50">{saving ? '保存中…' : '保存'}</button>
        {msg && <div className={`text-sm ${msg.ok ? 'text-emerald-400' : 'text-red-400'}`}>{msg.text}</div>}
      </div>
    </div>
  );
}

/** 去重对话框（厂商/模型通用）：列出重复分组 → 手动保留/清除 或 一键自动清除 */
function DupDialog({ type, onClose, onDone }: { type: 'models' | 'vendors'; onClose: () => void; onDone: () => void }) {
  const [groups, setGroups] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [keeps, setKeeps] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    apiFetch(`/api/admin/${type}/duplicates`).then(r => r.json()).then(d => {
      setGroups(d.groups || []);
      setTotal(d.total || 0);
      const k: Record<string, any> = {};
      for (const g of d.groups || []) k[g.key] = g.items[0];
      setKeeps(k);
    }).catch(() => {});
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const isModels = type === 'models';
  const title = isModels ? '模型去重' : '厂商去重';

  const autoClean = async () => {
    if (!confirm(`${title}：将自动保留每组最优记录，删除其余 ${groups.reduce((a, g) => a + g.items.length - 1, 0)} 条。确定继续？`)) return;
    setBusy(true); setMsg('');
    try {
      const resp = await apiFetch(`/api/admin/${type}/duplicates/auto`, { method: 'POST' });
      const d = await resp.json();
      setMsg(d.ok ? `自动清除完成：删除 ${d.removed} 条重复记录` : (d.error || '操作失败'));
      load();
      onDone();
    } finally { setBusy(false); }
  };

  const manualClean = async () => {
    let removed = 0;
    setBusy(true); setMsg('');
    try {
      for (const g of groups) {
        const keepId = keeps[g.key]?.id;
        if (!keepId) continue;
        const deleteIds = g.items.filter((it: any) => it.id !== keepId).map((it: any) => it.id);
        if (deleteIds.length === 0) continue;
        const resp = await apiFetch(`/api/admin/${type}/duplicates/merge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keepId, deleteIds }),
        });
        const d = await resp.json();
        removed += d.removed?.length || 0;
      }
      setMsg(`手动清除完成：删除 ${removed} 条重复记录`);
      load();
      onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-10">
      <div className="w-full max-w-3xl rounded-xl border border-gray-700 bg-[#111318] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title} <span className="text-sm text-gray-500">发现 {total} 组重复</span></h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-lg leading-none">✕</button>
        </div>

        {total === 0 && !busy && (
          <div className="text-sm text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">未发现重复记录，数据健康。</div>
        )}

        {groups.map((g: any) => (
          <div key={g.key} className="mb-4 rounded-lg border border-gray-700/70 bg-gray-900/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono text-amber-400 truncate max-w-[70%]">{g.key}</span>
              <span className="text-xs text-gray-500">{g.items.length} 条</span>
            </div>
            <div className="space-y-2">
              {g.items.map((it: any, i: number) => (
                <label key={isModels ? it.id : it.id} className={`flex items-start gap-3 rounded border px-3 py-2 text-xs cursor-pointer ${keeps[g.key]?.id === it.id ? 'border-cyan-500/60 bg-cyan-500/5' : 'border-gray-700/60'}`}>
                  <input
                    type="radio"
                    checked={keeps[g.key]?.id === it.id}
                    onChange={() => setKeeps(k => ({ ...k, [g.key]: it }))}
                    className="mt-0.5 accent-cyan-500"
                  />
                  <span className="flex-1 min-w-0">
                    {isModels ? (
                      <>
                        <span className="text-gray-200 font-medium">{it.platform} / {it.model_id}</span>
                        <span className="ml-2 text-gray-500">{it.display_name}</span>
                        <span className="ml-2 text-cyan-400">{it.free_quota_label || ''}</span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${it.enabled ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-500 bg-gray-600/20'}`}>{it.enabled ? '启用' : '停用'}</span>
                        <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[it.availability_status] || STATUS_COLORS.unknown}`}>{it.availability_status}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-200 font-medium">{it.name}</span>
                        <span className="ml-2 text-gray-500 font-mono truncate">{it.base_url}</span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${it.status === 'active' ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-400 bg-gray-600/20'}`}>{it.status}</span>
                      </>
                    )}
                  </span>
                  <span className="text-gray-600 shrink-0">保留</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-5 flex items-center gap-3">
          <button onClick={manualClean} disabled={busy || total === 0} className="px-5 py-2.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 disabled:opacity-40">
            {busy ? '处理中…' : '按所选保留项清除'}
          </button>
          <button onClick={autoClean} disabled={busy || total === 0} className="px-5 py-2.5 bg-amber-500/15 text-amber-400 rounded-lg text-sm hover:bg-amber-500/25 disabled:opacity-40">
            {busy ? '处理中…' : '自动清除（推荐）'}
          </button>
          <button onClick={onClose} disabled={busy} className="px-5 py-2.5 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 disabled:opacity-40">关闭</button>
        </div>
        {msg && <div className="mt-3 text-sm text-emerald-400">{msg}</div>}
        <p className="mt-3 text-xs text-gray-500">保留规则（自动清除）：启用/状态已知/信息更全/更新更近的记录优先保留；被删记录的额度、上下文等更优信息会自动并入保留条。删除同步写入墓碑，双机不会回灌。</p>
      </div>
    </div>
  );
}

/* ================= 下载中心：客户端软件管理 ================= */
const PLATFORM_LABELS: Record<string, string> = {
  windows: 'Windows', macos: 'macOS', linux: 'Linux', android: 'Android', ios: 'iOS', other: '其他',
};

function fmtSize(n: number): string {
  if (!n) return '-';
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function DownloadsTab() {
  const [items, setItems] = useState<any[]>([]);
  const [name, setName] = useState('CXAI-FreeRouter 客户端');
  const [version, setVersion] = useState('1.0.0');
  const [platform, setPlatform] = useState('windows');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [publicBase, setPublicBase] = useState('');

  const load = useCallback(() => {
    apiFetch('/api/admin/downloads').then(r => r.json()).then(d => setItems(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch('/api/admin/config/public').then(r => r.json()).then(d => { if (d.publicBaseUrl) setPublicBase(d.publicBaseUrl); }).catch(() => {});
  }, []);

  const upload = async () => {
    setErr(''); setMsg('');
    if (!file) { setErr('请选择要上传的安装包文件'); return; }
    if (!name.trim() || !version.trim()) { setErr('请填写名称与版本'); return; }
    setBusy(true);
    try {
      const qs = new URLSearchParams({ name: name.trim(), version: version.trim(), platform, description: description.trim(), file_name: file.name }).toString();
      const resp = await fetch(adminApi(`/api/admin/downloads?${qs}`), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      const d = await resp.json();
      if (!resp.ok) { setErr(d.error || '上传失败'); return; }
      setMsg(`上传成功：${d.download?.name || name} v${d.download?.version || version}`);
      setFile(null);
      load();
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally { setBusy(false); }
  };

  const toggleStatus = async (it: any) => {
    const resp = await apiFetch(`/api/admin/downloads/${it.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: it.status === 'active' ? 'hidden' : 'active' }),
    });
    if (resp.ok) load();
  };

  const remove = async (it: any) => {
    if (!confirm(`确认删除「${it.name} v${it.version}」？文件与记录将一并删除。`)) return;
    const resp = await apiFetch(`/api/admin/downloads/${it.id}`, { method: 'DELETE' });
    const d = await resp.json();
    if (!resp.ok) { alert(d.error || '删除失败'); return; }
    load();
  };

  const copyLink = (it: any) => {
    const link = `${publicBase}/api/catalog/downloads/${it.id}/file`;
    navigator.clipboard?.writeText(link).then(() => setMsg(`已复制下载链接：${link}`)).catch(() => setErr('复制失败，请手动复制'));
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-800 bg-[#0b1020]/80 p-5">
        <h2 className="text-lg font-semibold mb-1">上传客户端安装包</h2>
        <p className="text-xs text-gray-500 mb-4">上传后前台可通过「软件下载」区块展示；支持 exe/msi/zip/7z/dmg/apk 等格式，单文件上限 300MB。</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="名称（如 CXAI-FreeRouter 客户端）" className="rounded-lg border border-gray-700 bg-[#0f172a] px-3 py-2 text-sm outline-none focus:border-cyan-400" />
          <input value={version} onChange={e => setVersion(e.target.value)} placeholder="版本（如 1.0.0）" className="rounded-lg border border-gray-700 bg-[#0f172a] px-3 py-2 text-sm outline-none focus:border-cyan-400" />
          <select value={platform} onChange={e => setPlatform(e.target.value)} className="rounded-lg border border-gray-700 bg-[#0f172a] px-3 py-2 text-sm outline-none focus:border-cyan-400">
            {Object.entries(PLATFORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="rounded-lg border border-gray-700 bg-[#0f172a] px-3 py-2 text-sm text-gray-400 file:mr-3 file:rounded file:border-0 file:bg-cyan-500/20 file:px-3 file:py-1 file:text-cyan-300" />
        </div>
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="描述（可选）" rows={2} className="mt-3 w-full rounded-lg border border-gray-700 bg-[#0f172a] px-3 py-2 text-sm outline-none focus:border-cyan-400" />
        <div className="mt-4 flex items-center gap-4">
          <button onClick={upload} disabled={busy} className="rounded-lg bg-cyan-500/20 px-5 py-2 text-sm text-cyan-400 hover:bg-cyan-500/30 disabled:opacity-40">{busy ? '上传中…' : '上传文件'}</button>
          {file && <span className="text-xs text-gray-400">{file.name} · {fmtSize(file.size)}</span>}
          {msg && <span className="text-xs text-emerald-400">{msg}</span>}
          {err && <span className="text-xs text-red-400">{err}</span>}
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-[#0b1020]/80 p-5">
        <h2 className="text-lg font-semibold mb-4">已上传（{items.length}）</h2>
        {items.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">暂无上传记录</div>
        ) : (
          <div className="space-y-2">
            {items.map(it => (
              <div key={it.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-800 bg-[#0f172a] p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-100">{it.name}</span>
                    <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 text-[10px] text-cyan-400">v{it.version}</span>
                    <span className="rounded bg-gray-700/40 px-1.5 py-0.5 text-[10px] text-gray-400">{PLATFORM_LABELS[it.platform] || it.platform}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${it.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-600/40 text-gray-400'}`}>{it.status === 'active' ? '已发布' : '已隐藏'}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">{it.file_name} · {fmtSize(it.file_size)}{it.file_hash ? ` · SHA-256 ${it.file_hash.slice(0, 12)}…` : ''}</div>
                  {it.description && <div className="mt-0.5 text-xs text-gray-400 truncate">{it.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyLink(it)} className="rounded bg-sky-500/15 px-3 py-1.5 text-xs text-sky-400 hover:bg-sky-500/25">复制链接</button>
                  <button onClick={() => toggleStatus(it)} className="rounded bg-gray-700/50 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-600/50">{it.status === 'active' ? '隐藏' : '发布'}</button>
                  <button onClick={() => remove(it)} className="rounded bg-red-500/15 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/25">删除</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
/* ================= 激活接入资料导出 ================= */
function ActivateGuideDialog({ plan, onClose }: { plan: any; onClose: () => void }) {
  const [tab, setTab] = useState<'curl' | 'node' | 'python'>('curl');
  const [copied, setCopied] = useState('');
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://free.ldcxwh.cn';
  const isCatalog = plan.kind === 'catalog';
  const durationLabel = plan.duration_days > 0 ? `${plan.duration_days} 天` : '永久';

  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(''), 1500); }).catch(() => {});
  };

  const downloadFile = (filename: string, content: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportMarkdown = () => {
    const md = `# ${plan.name} · 激活接入资料

## 产品信息
| 项目 | 值 |
|------|-----|
| 套餐代码 | \`${plan.code}\` |
| 类型 | ${plan.kind} |
| 产品标识 | \`${plan.product}\` |
| 时长 | ${durationLabel} |
| 激活方式 | ${isCatalog ? '绑邮箱' : '一卡一机（绑机器码）'} |
| 卡密前缀 | \`${isCatalog ? 'CXAI-' : 'CXAR-'}\` |

## 激活接口

\`\`\`
POST ${base}/api/card/activate
\`\`

**请求参数（JSON body）：**
${activateParams.map(p => `| 参数 | 必填 | 说明 |\\n|------|------|------|\\n| \`${p.name}\` | ${p.req ? '是' : '否'} | ${p.desc} |`).join('')}

**返回（JSON）：**
\`\`\`json
${isCatalog ? '{ "product": "...", "durationDays": 365, "expiresAt": "...", "syncToken": "..." }' : '{ "ok": true, "kind": "...", "plan": "...", "lifetime": false, "expiresAt": "...", "message": "授权激活成功" }'}
\`\`\`
${!isCatalog ? `
## 授权校验接口

\`\`\`
GET ${base}/api/license/check?cardCode=CXAR-XXXX&machineId=你的机器码
\`\`

**返回：**
\`\`\`json
{ "valid": true, "plan": "...", "lifetime": false, "expiresAt": "..." }
\`\`\`
\`valid=false\` 表示未授权/过期/换机

## 机器码（machineId）生成建议
机器码是唯一标识设备的字符串（≤200 字符），需保证同一设备稳定不变、不同设备不同。
建议取硬件指纹组合后 SHA256 哈希：
- MAC 地址 + 硬盘序列号 + CPU ID → SHA256 → 取前 32-64 位十六进制
- Node.js: \`crypto.createHash('sha256').update(mac + diskSn + cpuId).digest('hex').slice(0, 64)\`
` : ''}
## 示例代码

### cURL
\`\`\`bash
${curlCode}
\`\`\`

### Node.js
\`\`\`javascript
${nodeCode}
\`\`\`

### Python
\`\`\`python
${pythonCode}
\`\`\`

## 接入流程
1. 在后台「卡密管理」生成该产品的卡密（选「${plan.name}」）
2. 将卡密分发给用户（随订单/邮件/手动）
3. ${isCatalog ? '用户在网站「定价方案 → 兑换码激活」输入卡密 + 邮箱激活' : '用户在你的软件中输入卡密，软件采集机器码后调激活接口'}
${!isCatalog ? '4. 软件每次启动调校验接口确认授权有效，失效则限制功能\n5. ' : '4. '}卡密一${isCatalog ? '邮箱' : '机'}绑定，不可换${isCatalog ? '邮箱' : '设备'}（换${isCatalog ? '邮箱' : '机'}需联系客服）

---
Generated at ${new Date().toISOString()}
`;
    downloadFile(`${plan.code}-activation-guide.md`, md, 'text/markdown');
  };

  const exportJson = () => {
    const contract = {
      product: { code: plan.code, name: plan.name, kind: plan.kind, product: plan.product, durationDays: plan.duration_days, displayPrice: plan.display_price },
      activation: {
        endpoint: `${base}/api/card/activate`,
        method: 'POST',
        contentType: 'application/json',
        params: activateParams,
        response: isCatalog
          ? { product: 'string', durationDays: 'number', expiresAt: 'string', syncToken: 'string' }
          : { ok: 'boolean', kind: 'string', plan: 'string', lifetime: 'boolean', expiresAt: 'string', message: 'string' },
      },
      ...(isCatalog ? {} : {
        licenseCheck: {
          endpoint: `${base}/api/license/check`,
          method: 'GET',
          params: [
            { name: 'cardCode', req: true, desc: '卡密' },
            { name: 'machineId', req: true, desc: '机器码' },
          ],
          response: { valid: 'boolean', plan: 'string', lifetime: 'boolean', expiresAt: 'string' },
        },
        machineIdGuide: 'MAC + 硬盘序列号 + CPU ID → SHA256 → 取前 32-64 位十六进制',
      }),
      cardPrefix: isCatalog ? 'CXAI-' : 'CXAR-',
      activateMode: isCatalog ? 'bindEmail' : 'bindMachineId',
      examples: { curl: curlCode, node: nodeCode, python: pythonCode },
      generatedAt: new Date().toISOString(),
    };
    downloadFile(`${plan.code}-activation-contract.json`, JSON.stringify(contract, null, 2), 'application/json');
  };


  const activateParams = isCatalog
    ? [{ name: 'cardCode', req: true, desc: '卡密（CXAI- 开头）' }, { name: 'email', req: true, desc: '用户邮箱（绑定）' }]
    : [{ name: 'cardCode', req: true, desc: '卡密（CXAR- 开头）' }, { name: 'machineId', req: true, desc: '机器码（≤200字符，唯一标识设备，一卡一机）' }];

  const curlCode = isCatalog
    ? `# 激活（绑邮箱）
curl -X POST ${base}/api/card/activate \\
  -H "Content-Type: application/json" \\
  -d '{"cardCode":"CXAI-XXXX-XXXX-XXXX-XXXX","email":"user@example.com"}'`
    : `# 激活（绑机器码，一卡一机）
curl -X POST ${base}/api/card/activate \\
  -H "Content-Type: application/json" \\
  -d '{"cardCode":"CXAR-XXXX-XXXX-XXXX-XXXX","machineId":"你的机器码"}'

# 校验授权（软件启动时调用）
curl "${base}/api/license/check?cardCode=CXAR-XXXX-XXXX-XXXX-XXXX&machineId=你的机器码"`;

  const nodeCode = isCatalog
    ? `const resp = await fetch("${base}/api/card/activate", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ cardCode: "CXAI-XXXX-XXXX-XXXX-XXXX", email: "user@example.com" }),
});
const data = await resp.json();
// { product, durationDays, expiresAt, syncToken }`
    : `// 激活（一卡一机）
const resp = await fetch("${base}/api/card/activate", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ cardCode: "CXAR-XXXX-XXXX-XXXX-XXXX", machineId: "你的机器码" }),
});
const data = await resp.json();
// { ok: true, kind, plan, lifetime, expiresAt, message }

// 校验授权
const check = await fetch(\`${base}/api/license/check?cardCode=${'${cardCode}'}&machineId=${'${machineId}'}\`);
const r = await check.json();
// { valid: true, plan, lifetime, expiresAt }`;

  const pythonCode = isCatalog
    ? `import requests

resp = requests.post("${base}/api/card/activate",
    json={"cardCode": "CXAI-XXXX-XXXX-XXXX-XXXX", "email": "user@example.com"})
data = resp.json()
# {'product': ..., 'durationDays': ..., 'expiresAt': ..., 'syncToken': ...}`
    : `import requests

# 激活（一卡一机）
resp = requests.post("${base}/api/card/activate",
    json={"cardCode": "CXAR-XXXX-XXXX-XXXX-XXXX", "machineId": "你的机器码"})
data = resp.json()
# {'ok': True, 'kind': ..., 'plan': ..., 'lifetime': ..., 'expiresAt': ..., 'message': ...}

# 校验授权
check = requests.get("${base}/api/license/check",
    params={"cardCode": "CXAR-XXXX-XXXX-XXXX-XXXX", "machineId": "你的机器码"})
r = check.json()
# {'valid': True, 'plan': ..., 'lifetime': ..., 'expiresAt': ...}`;

  const code = tab === 'curl' ? curlCode : tab === 'node' ? nodeCode : pythonCode;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-cyan-300">激活接入资料 · {plan.name}</h2>
          <div className="flex items-center gap-2">
            <button onClick={exportMarkdown} className="px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-xs flex items-center gap-1">导出 Markdown</button>
            <button onClick={exportJson} className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-xs flex items-center gap-1">导出 JSON</button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
          </div>
        </div>

        <div className="space-y-4">
          <section>
            <h3 className="text-sm font-semibold text-gray-300 mb-2">产品信息</h3>
            <div className="bg-gray-950 rounded p-3 text-xs text-gray-400 grid grid-cols-2 gap-2">
              <div>套餐代码：<span className="text-cyan-400 font-mono">{plan.code}</span></div>
              <div>类型：<span className="text-cyan-400">{plan.kind}</span></div>
              <div>产品标识：<span className="text-cyan-400 font-mono">{plan.product}</span></div>
              <div>时长：<span className="text-cyan-400">{durationLabel}</span></div>
              <div>激活方式：<span className="text-cyan-400">{isCatalog ? '绑邮箱' : '一卡一机（绑机器码）'}</span></div>
              <div>卡密前缀：<span className="text-cyan-400 font-mono">{isCatalog ? 'CXAI-' : 'CXAR-'}</span></div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-300 mb-2">激活接口</h3>
            <div className="bg-gray-950 rounded p-3 text-xs space-y-2">
              <div><span className="text-emerald-400 font-mono">POST</span> <span className="text-gray-300 font-mono">{base}/api/card/activate</span></div>
              <div className="text-gray-500">请求参数（JSON body）：</div>
              <table className="w-full text-xs">
                <thead><tr className="text-gray-600"><th className="text-left py-1">参数</th><th className="text-left py-1">必填</th><th className="text-left py-1">说明</th></tr></thead>
                <tbody>
                  {activateParams.map(p => (
                    <tr key={p.name} className="text-gray-400">
                      <td className="py-1 font-mono text-cyan-400">{p.name}</td>
                      <td className="py-1">{p.req ? '是' : '否'}</td>
                      <td className="py-1">{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-gray-500 mt-2">返回（JSON）：</div>
              <pre className="text-gray-400 font-mono whitespace-pre-wrap">{isCatalog
                ? '{ product, durationDays, expiresAt, syncToken }'
                : '{ ok: true, kind, plan, lifetime, expiresAt, message }'}</pre>
            </div>
          </section>

          {!isCatalog && (
            <section>
              <h3 className="text-sm font-semibold text-gray-300 mb-2">授权校验接口</h3>
              <div className="bg-gray-950 rounded p-3 text-xs space-y-1">
                <div><span className="text-emerald-400 font-mono">GET</span> <span className="text-gray-300 font-mono">{base}/api/license/check</span></div>
                <div className="text-gray-500">Query 参数：<span className="text-cyan-400 font-mono">cardCode</span>、<span className="text-cyan-400 font-mono">machineId</span></div>
                <div className="text-gray-500">返回：<span className="text-gray-400 font-mono">{'{ valid, plan, lifetime, expiresAt }'}</span>（valid=false 表示未授权/过期/换机）</div>
              </div>
            </section>
          )}

          {!isCatalog && (
            <section>
              <h3 className="text-sm font-semibold text-gray-300 mb-2">机器码（machineId）生成建议</h3>
              <div className="bg-gray-950 rounded p-3 text-xs text-gray-400 leading-relaxed">
                机器码是唯一标识设备的字符串（≤200 字符），需保证同一设备稳定不变、不同设备不同。建议取硬件指纹组合后 SHA256 哈希：<br/>
                <span className="text-gray-500">MAC 地址 + 硬盘序列号 + CPU ID → SHA256 → 取前 32-64 位十六进制</span><br/>
                <span className="text-gray-500">Node.js 示例：crypto.createHash('sha256').update(mac + diskSn + cpuId).digest('hex').slice(0, 64)</span>
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-300">示例代码</h3>
              <div className="flex gap-1">
                {(['curl', 'node', 'python'] as const).map(t => (
                  <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 rounded text-xs ${tab === t ? 'bg-cyan-500/20 text-cyan-300' : 'bg-gray-800 text-gray-500 hover:text-gray-300'}`}>
                    {t === 'curl' ? 'cURL' : t === 'node' ? 'Node.js' : 'Python'}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative">
              <pre className="bg-gray-950 rounded p-3 text-xs text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-72 overflow-y-auto">{code}</pre>
              <button onClick={() => copy(code, 'code')} className="absolute top-2 right-2 text-xs text-cyan-400 hover:text-cyan-300 bg-gray-800/80 px-2 py-1 rounded">{copied === 'code' ? '✓' : '复制'}</button>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-300 mb-2">接入流程</h3>
            <ol className="bg-gray-950 rounded p-3 text-xs text-gray-400 space-y-1 list-decimal list-inside">
              <li>在后台「卡密管理」生成该产品的卡密（选「{plan.name}」）</li>
              <li>将卡密分发给用户（随订单/邮件/手动）</li>
              <li>{isCatalog ? '用户在网站「定价方案 → 兑换码激活」输入卡密 + 邮箱激活' : '用户在你的软件中输入卡密，软件采集机器码后调激活接口'}</li>
              {!isCatalog && <li>软件每次启动调校验接口确认授权有效，失效则限制功能</li>}
              <li>卡密一{isCatalog ? '邮箱' : '机'}绑定，不可换{isCatalog ? '邮箱' : '设备'}（换{isCatalog ? '邮箱' : '机'}需联系客服）</li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ================= 其它产品管理（kind=software，一站式：CRUD + 生成卡密 + 接入资料） ================= */
function ProductsTab({ goTab }: { goTab: (t: Tab, filter?: any) => void }) {
  const [plans, setPlans] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({ cards: [], subs: [], orders: [] });
  const [editing, setEditing] = useState<any | null>(null);
  const [guidePlan, setGuidePlan] = useState<any | null>(null);
  const [genPlan, setGenPlan] = useState<any | null>(null);
  const [genCount, setGenCount] = useState('10');
  const [genResult, setGenResult] = useState<string[]>([]);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    apiFetch('/api/admin/plans').then(r => r.json()).then((d: any[]) => setPlans(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/api/admin/products/stats').then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const KIND_LABELS: Record<string, string> = { catalog: '模型更新', router: '企业版', software: '软件授权' };
  const cardCount = (p: any) => {
    const rows = (stats.cards || []).filter((c: any) => c.kind === p.kind && c.product === p.product);
    const total = rows.reduce((s: number, c: any) => s + c.c, 0);
    const unused = rows.filter((c: any) => c.status === 'unused').reduce((s: number, c: any) => s + c.c, 0);
    return { total, unused };
  };
  const subCount = (p: any) => (stats.subs || []).filter((s: any) => s.plan === p.product).reduce((sum: number, s: any) => sum + s.c, 0);
  const orderCount = (p: any) => (stats.orders || []).filter((o: any) => o.plan_code === p.code).reduce((sum: number, o: any) => sum + o.c, 0);

  const save = async () => {
    setMsg('');
    const isEdit = !!editing.id;
    const body = {
      code: editing.code, name: editing.name, kind: editing.kind || 'software', product: editing.product,
      durationDays: Number(editing.duration_days), priceCents: Number(editing.price_cents),
      displayPrice: editing.display_price, description: editing.description,
      features: (editing.features || '').split('\n').map((s: string) => s.trim()).filter(Boolean),
      sortOrder: Number(editing.sort_order || 99), poolWarnThreshold: Number(editing.pool_warn_threshold || 5),
      enabled: editing.enabled ? 1 : 0,
    };
    const resp = await apiFetch(`/api/admin/plans${isEdit ? `/${editing.id}` : ''}`, {
      method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const d = await resp.json();
    if (!resp.ok) { setMsg(d.error || '保存失败'); return; }
    setMsg('保存成功'); setEditing(null); load();
  };

  const toggle = async (p: any) => {
    await apiFetch(`/api/admin/plans/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: p.enabled ? 0 : 1 }) });
    load();
  };

  const remove = async (p: any) => {
    if (!confirm(`确认删除产品「${p.name}」？\n已生成的卡密不受影响，但将无法再生成新卡密。`)) return;
    await apiFetch(`/api/admin/plans/${p.id}`, { method: 'DELETE' });
    load();
  };

  const doGenerate = async () => {
    if (!genPlan) return;
    setMsg('');
    const r = await apiFetch('/api/admin/cards/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planCode: genPlan.code, count: parseInt(genCount) || 1 }),
    });
    const d = await r.json();
    if (!r.ok) { setMsg(d.error || '生成失败'); return; }
    setGenResult(d.cards || []);
  };

  const copy = (text: string) => { navigator.clipboard?.writeText(text); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">产品管理 <span className="text-sm text-gray-500">({plans.length})</span></h1>
          <p className="text-xs text-gray-500 mt-1">统一管理所有产品（模型更新/企业版/软件授权）。增加产品 → 生成卡密 → 用户购买/激活 → 查看授权与订单。点击卡密/订阅/订单列数字可跳转查看明细。</p>
        </div>
        <button onClick={() => setEditing({ code: '', name: '', kind: 'software', product: 'yearly', duration_days: 365, price_cents: 0, display_price: '', description: '', features: '', enabled: 1, sort_order: 99, pool_warn_threshold: 5 })}
          className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30">新增产品</button>
      </div>

      {editing && (
        <div className="bg-gray-900/50 border border-cyan-500/30 rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold text-cyan-300">{editing.id ? '编辑产品' : '新增产品'}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input value={editing.code || ''} disabled={!!editing.id} onChange={e => setEditing({ ...editing, code: e.target.value })} placeholder="代码 (如 myapp_pro)" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm disabled:opacity-50" />
            <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="产品名称" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <select value={editing.kind || 'software'} onChange={e => setEditing({ ...editing, kind: e.target.value })} className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm">
              <option value="catalog">模型更新</option><option value="router">企业版</option><option value="software">软件授权</option>
            </select>
            <input value={editing.product || ''} onChange={e => setEditing({ ...editing, product: e.target.value })} placeholder="产品标识 (如 pro_yearly)" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <input value={editing.duration_days ?? 365} onChange={e => setEditing({ ...editing, duration_days: e.target.value })} placeholder="时长(天,0=永久)" type="number" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <input value={editing.price_cents ?? 0} onChange={e => setEditing({ ...editing, price_cents: e.target.value })} placeholder="价格(分)" type="number" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <input value={editing.display_price || ''} onChange={e => setEditing({ ...editing, display_price: e.target.value })} placeholder="展示价 (¥99)" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <input value={editing.sort_order ?? 99} onChange={e => setEditing({ ...editing, sort_order: e.target.value })} placeholder="排序" type="number" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
            <input value={editing.pool_warn_threshold ?? 5} onChange={e => setEditing({ ...editing, pool_warn_threshold: e.target.value })} placeholder="卡密池告警阈值" type="number" className="px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
          </div>
          <input value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="产品描述" className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
          <textarea value={editing.features || ''} onChange={e => setEditing({ ...editing, features: e.target.value })} placeholder="特性列表（每行一条）" rows={3} className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={!!editing.enabled} onChange={e => setEditing({ ...editing, enabled: e.target.checked })} className="accent-cyan-500" /> 启用</label>
            <button onClick={save} className="px-4 py-1.5 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30">保存</button>
            <button onClick={() => setEditing(null)} className="px-4 py-1.5 bg-gray-800 text-gray-300 rounded text-sm hover:bg-gray-700">取消</button>
            {msg && <span className="text-xs text-emerald-400">{msg}</span>}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-gray-500 border-b border-gray-800">
            <th className="py-2 text-left">名称</th><th className="py-2 text-left">类型</th><th className="py-2 text-left">代码</th>
            <th className="py-2 text-left">产品标识</th><th className="py-2 text-right">价格</th>
            <th className="py-2 text-center">时长</th><th className="py-2 text-center">卡密</th>
            <th className="py-2 text-center">订阅</th><th className="py-2 text-center">订单</th>
            <th className="py-2 text-center">状态</th><th className="py-2 text-center">操作</th>
          </tr></thead>
          <tbody>
            {plans.map(p => {
              const cc = cardCount(p); const sc = subCount(p); const oc = orderCount(p);
              return (
              <tr key={p.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-200">{p.name}</td>
                <td className="py-2 text-gray-400 text-xs">{KIND_LABELS[p.kind] || p.kind}</td>
                <td className="py-2 text-gray-400 font-mono text-xs">{p.code}</td>
                <td className="py-2 text-gray-400 font-mono text-xs">{p.product}</td>
                <td className="py-2 text-right text-cyan-400">{p.display_price || `${p.price_cents}分`}</td>
                <td className="py-2 text-center text-gray-400">{p.duration_days ? (p.duration_days >= 365 ? `${Math.round(p.duration_days / 365)}年` : `${p.duration_days}天`) : '永久'}</td>
                <td className="py-2 text-center"><button onClick={() => goTab('cards', { kind: p.kind, product: p.product })} className="text-xs text-emerald-400 hover:text-emerald-300 hover:underline" title="未用/总数">{cc.unused}/{cc.total}</button></td>
                <td className="py-2 text-center"><button onClick={() => goTab('subs', { plan: p.product })} className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline">{sc}</button></td>
                <td className="py-2 text-center"><button onClick={() => goTab('orders', { planCode: p.code })} className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline">{oc}</button></td>
                <td className="py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${p.enabled ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-400 bg-gray-500/10'}`}>{p.enabled ? '启用' : '停用'}</span></td>
                <td className="py-2 text-center whitespace-nowrap">
                  <button onClick={() => setEditing({ ...p, features: (() => { try { return JSON.parse(p.features).join('\n'); } catch { return ''; } })() })} className="text-xs text-cyan-400 hover:text-cyan-300 mr-1">编辑</button>
                  <button onClick={() => setGenPlan(p)} className="text-xs text-emerald-400 hover:text-emerald-300 mr-1">发卡</button>
                  <button onClick={() => setGuidePlan(p)} className="text-xs text-sky-400 hover:text-sky-300 mr-1">资料</button>
                  <button onClick={() => toggle(p)} className="text-xs text-yellow-400 hover:text-yellow-300 mr-1">{p.enabled ? '停' : '启'}</button>
                  <button onClick={() => remove(p)} className="text-xs text-red-400 hover:text-red-300">删</button>
                </td>
              </tr>
              );
            })}
            {plans.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-gray-600">暂无产品，点击右上角「新增产品」添加。</td></tr>}
          </tbody>
        </table>
      </div>

      {genPlan && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => { setGenPlan(null); setGenResult([]); }}>
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-emerald-300">生成卡密 · {genPlan.name}</h2>
              <button onClick={() => { setGenPlan(null); setGenResult([]); }} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
            </div>
            {genResult.length === 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">卡密前缀 CXAR-，一卡一机，时长 {genPlan.duration_days > 0 ? `${genPlan.duration_days} 天` : '永久'}。</div>
                <div className="flex gap-2">
                  <input value={genCount} onChange={e => setGenCount(e.target.value)} type="number" min="1" max="500" placeholder="数量" className="flex-1 px-3 py-2 bg-gray-900 border border-gray-800 rounded text-sm" />
                  <button onClick={doGenerate} className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded text-sm hover:bg-emerald-500/30">生成</button>
                </div>
                {msg && <div className="text-xs text-red-400">{msg}</div>}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-emerald-400 text-sm">已生成 {genResult.length} 个卡密：</div>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {genResult.map((code, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-1 bg-gray-950 rounded text-xs text-emerald-300 font-mono">{code}</code>
                      <button onClick={() => copy(code)} className="text-xs text-cyan-400 hover:text-cyan-300">复制</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => copy(genResult.join('\n'))} className="text-xs text-cyan-400 hover:text-cyan-300">复制全部</button>
                  <button onClick={() => { setGenPlan(null); setGenResult([]); }} className="text-xs text-gray-500 hover:text-gray-300">关闭</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {guidePlan && <ActivateGuideDialog plan={guidePlan} onClose={() => setGuidePlan(null)} />}
    </div>
  );
}

/* ================= 邮件队列 ================= */

function EmailTab() {
  const [items, setItems] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    const q = filter !== 'all' ? `?status=${filter}` : '';
    apiFetch(`/api/admin/email-queue${q}`).then(r => r.json()).then(d => setItems(d.data || [])).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">邮件队列 <span className="text-sm text-gray-500">({items.length})</span></h1>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded text-sm">
          <option value="all">全部</option><option value="pending">待发送</option><option value="sent">已发送</option><option value="failed">失败</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-gray-500 border-b border-gray-800">
            <th className="py-2 text-left">收件人</th><th className="py-2 text-left">主题</th><th className="py-2 text-center">状态</th>
            <th className="py-2 text-center">重试</th><th className="py-2 text-left">错误</th><th className="py-2 text-left">创建</th><th className="py-2 text-left">发送</th>
          </tr></thead>
          <tbody>
            {items.map((m: any) => (
              <tr key={m.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-300 text-xs">{m.to_email}</td>
                <td className="py-2 text-gray-400 text-xs">{m.subject}</td>
                <td className="py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${m.status === 'sent' ? 'text-emerald-400 bg-emerald-500/10' : m.status === 'failed' ? 'text-red-400 bg-red-500/10' : 'text-yellow-400 bg-yellow-500/10'}`}>{m.status}</span></td>
                <td className="py-2 text-center text-gray-400">{m.tries}</td>
                <td className="py-2 text-red-400 text-xs truncate max-w-[200px]">{m.last_error || '-'}</td>
                <td className="py-2 text-gray-500 text-xs">{m.created_at?.slice(0, 19)}</td>
                <td className="py-2 text-gray-500 text-xs">{m.sent_at?.slice(0, 19) || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length === 0 && <div className="text-gray-600 mt-4">队列为空</div>}
    </div>
  );
}